import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Save, ArrowLeft, RotateCcw, Plus, Trash2, FileText, CheckCircle2, History, Download, Briefcase, ArrowUp, ArrowDown, Award, Eye, AlertTriangle, ChevronDown, GripVertical } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/utils/formatINR";
import { computeNpd, computeAutoGrossWeight, deriveMonthlyRmBlock, deriveTooling } from "@/utils/npdCalc";
import { autoTonnageForProcess, STATION_TONNAGES } from "@/utils/processStation";
import MaterialGradePicker from "@/components/MaterialGradePicker";
import OverheadsTable from "@/components/OverheadsTable";
import LiveCalcPanel from "@/components/LiveCalcPanel";
import { rmcIncompleteReasons } from "@/utils/rmcComplete";
import DrawingUpload from "@/components/DrawingUpload";
import ScreenshotUpload from "@/components/ScreenshotUpload";
import RfqMailUpload from "@/components/RfqMailUpload";
import SourcingBlock from "@/components/SourcingBlock";
import MiscFilesPanel from "@/components/MiscFilesPanel";
import TreatmentField from "@/components/TreatmentField";
import RoundCompareView from "@/components/RoundCompareView";
import DecimalInput from "@/components/DecimalInput";
import GroupedNumberInput from "@/components/GroupedNumberInput";

// Short labels for the machine-type badge shown beside the tonnage in the Process Cost table.
const MACHINE_TYPE_SHORT = { press: "Press", cnc: "CNC", vmc: "VMC", sliding_head: "Sliding", welding: "Weld", other: "Other" };

// Press/forming steps that physically run N-up in a multi-cavity tool (one stroke = N pieces).
// These get their "multi-cavity" flag auto-ticked when the part is marked multiple-cavity.
const isBlankingOp = (name) => /blank|progressive|forming|deep\s*draw|compound|piercing|coining|stamp/i.test(name || "");


const INITIAL = {
  customer_id: "", end_customer: "", part_number: "", part_name: "", drawing_number: "",
  model_name: "", application: "", category: "", category_other: "",
  customer_due_date: "", quote_validity_date: "", target_price: 0, remarks: "",
  unit_id: "",
  material_id: null, material_grade: "", thickness_mm: 0,
  rm_price_per_kg: 0, scrap_price_per_kg: 0,
  gross_weight: 0, gross_weight_overridden: false, cavity_count: 1, net_weight: 0,
  pitch: 0, strip_width: 0,
  sheet_width_mm: 1250, sheet_length_mm: 2500,
  gross_weight_method: "sheet",
  scrap_recovery_factor: 0.9,
  // Round 10 — stock form
  stock_form: "sheet_strip",
  rod_diameter_mm: 0, rod_length_mm: 0, rod_stock_length_mm: 3048, rod_end_loss_mm: 248,
  pipe_od_mm: 0, pipe_id_mm: 0, pipe_length_mm: 0,
  bar_side_mm: 0, bar_length_mm: 0,
  hex_across_flats_mm: 0, hex_length_mm: 0,
  processes: [], bop_items: [],
  surface_treatment: { mode: "per_piece", rate: 0, surface_area_sqin: null, notes: "" },
  heat_treatment:    { mode: "per_piece", rate: 0, surface_area_sqin: null, notes: "" },
  tool_maintenance_pct: 10,            // PERCENTAGE: 10 = 10%
  tool_maintenance_overridden: false, tool_maintenance_fixed: 0,
  tooling_cost: 0, tooling_life: 0, amortize_tooling: false,
  overheads: [], selling_price: 0,
  // Other One-time Development Cost (one-time only — not in per-pc Grand Total)
  other_dev_costs: [],
  // R46 — Section F: customer markups / discounts
  custom_adjustments: [], custom_adjustments_visible_in_quote: false,
  // Round 12 — Volumes & YoY
  yoy_reduction_pct: 0,
  project_life_years: 1,
  prototype_qty: 1,   // Prototype Costing only — per-piece quantity; display/record, not in compute()
  is_prototype: false,  // persisted so a saved prototype stays a prototype after reload
  // Project Scope renders the schedule at all times, so seed with Y1.
  volumes: [{ year_number: 1, year_label: "", annual_volume_pcs: 0, monthly_volume_pcs: 0 }],
  // Round 17 — YoY apply-years cap (null = no cap)
  yoy_apply_years: null,
  // R54 — per-year manual reduction % override: { "2": 3, "3": 5, ... }. Empty = use the
  // global rate for every year (identical to the old behaviour).
  yoy_per_year_pct: {},
};

export default function NpdForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const protoParam = searchParams.get("proto") === "1";
  const nav = useNavigate();
  const { user, canEditAll, hasRole, hasPermission } = useAuth();
  const hideMargin = user ? !user.view_margin : true;
  const canApprove = hasPermission("npd.approve");
  const canAward = hasPermission("npd.award");

  const [formData, setFormData] = useState(INITIAL);
  // Prototype mode: from the ?proto=1 URL param (new costing) OR the persisted flag (saved record).
  const isProto = protoParam || formData.is_prototype === true;
  // Stamp the flag on a NEW prototype so it persists (survives save + reload).
  useEffect(() => {
    if (protoParam && !isEdit) setFormData((p) => (p.is_prototype ? p : { ...p, is_prototype: true }));
  }, [protoParam, isEdit]);
  const [density, setDensity] = useState(7.85);
  const [customers, setCustomers] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  // Snapshot of the last loaded/saved form state — lets the Excel button know when
  // there are unsaved edits that wouldn't appear in a server-generated download.
  const baselineRef = useRef(null);
  const isDirty = () => baselineRef.current != null && JSON.stringify(formData) !== baselineRef.current;
  const [npdMeta, setNpdMeta] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [cloneSources, setCloneSources] = useState([]);
  const [cloneQuery, setCloneQuery] = useState("");
  const [cloneOpen, setCloneOpen] = useState(false);
  const [units, setUnits] = useState([]);
  const [showYoy, setShowYoy] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [comparingRound, setComparingRound] = useState(null); // shape: null | { left: roundId | "_current_", right: roundId | "_current_" }
  const [dupCheck, setDupCheck] = useState({ checking: false, matches: [] }); // Round 28

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/units").then((r) => setUnits(r.data || [])).catch(() => {});
    }
  }, [user?.role]);

  // Round 29 — non-blocking system-wide duplicate part-number lookup.
  // Fires on part_number alone (no customer gate). Debounced 400 ms.
  useEffect(() => {
    const part_number = (formData.part_number || "").trim();
    if (!part_number) {
      setDupCheck({ checking: false, matches: [] });
      return;
    }
    setDupCheck((p) => ({ ...p, checking: true }));
    const t = setTimeout(async () => {
      try {
        const params = { part_number };
        if (id && id !== "new") params.exclude_npd_id = id;
        const r = await api.get("/npd/_/check-duplicate", { params });
        setDupCheck({ checking: false, matches: r.data?.matches || [] });
      } catch {
        setDupCheck({ checking: false, matches: [] });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [formData.part_number, id]);

  useEffect(() => {
    if (isEdit) return;
    // Load up to 100 recent NPDs as clone candidates
    api.get("/npd", { params: { limit: 500 } })
      .then((r) => setCloneSources(r.data.npds || []))
      .catch(() => {});
  }, [isEdit]);

  const goBack = () => {
    if (window.history.length > 1) { nav(-1); return; }
    const from = searchParams.get("from");
    if (from === "project" && formData.project_id) { nav(`/projects/${formData.project_id}`, { replace: true }); return; }
    if (formData.project_id) { nav(`/projects/${formData.project_id}`, { replace: true }); return; }
    nav("/npd", { replace: true });
  };

  const cloneFromSource = async (src_npd_id) => {
    if (!src_npd_id) return;
    if (!window.confirm("Clone this NPD as a new draft? You'll be redirected to the new record.")) return;
    try {
      const r = await api.post(`/npd/${src_npd_id}/clone`);
      toast.success(`Cloned → ${r.data.npd_number}`);
      nav(`/npd/${r.data.npd_id}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/materials").then((r) => setMaterials(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) {
      // Load default overheads + processes for new NPD (Round 18)
      api.get("/settings/public").then((r) => {
        try {
          const oh = JSON.parse(r.data.default_overheads || "[]");
          const procs = JSON.parse(r.data.default_processes || "[]");
          setFormData((p) => ({ ...p, overheads: oh, processes: procs }));
        } catch {}
      });
    } else {
      (async () => {
        try {
          const r = await api.get(`/npd/${id}`);
          setNpdMeta({
            npd_number: r.data.npd_number,
            status: r.data.status,
            current_round: r.data.current_round,
            approved_for_export: r.data.approved_for_export,
            approved_at: r.data.approved_at,
            approved_by_name: r.data.approved_by_name,
            is_awarded: r.data.is_awarded,
            awarded_at: r.data.awarded_at,
            awarded_by: r.data.awarded_by,
            outcome: r.data.outcome || "pending",
            outcome_at: r.data.outcome_at,
            outcome_by: r.data.outcome_by,
            created_at: r.data.created_at,
          });
          // Migrate legacy fields → new shape (UI only; backend handles both)
          const merged = { ...INITIAL, ...r.data };
          // tool_maintenance_pct legacy decimal → percent
          if (merged.tool_maintenance_pct !== undefined && merged.tool_maintenance_pct !== null && merged.tool_maintenance_pct < 1.5) {
            merged.tool_maintenance_pct = merged.tool_maintenance_pct * 100;
          }
          // surface/heat treatment: ensure structured object exists
          if (!merged.surface_treatment || typeof merged.surface_treatment !== "object" || merged.surface_treatment.mode === undefined) {
            merged.surface_treatment = {
              mode: "per_piece",
              rate: Number(r.data.surface_treatment_cost) || 0,
              surface_area_sqin: null,
              notes: r.data.surface_treatment_notes || "",
            };
          }
          if (!merged.heat_treatment || typeof merged.heat_treatment !== "object" || merged.heat_treatment.mode === undefined) {
            merged.heat_treatment = {
              mode: "per_piece",
              rate: Number(r.data.heat_treatment_cost) || 0,
              surface_area_sqin: null,
              notes: r.data.heat_treatment_notes || "",
            };
          }
          // Reshape volumes to match project_life_years. year_label left blank —
          // the user types SOP year into Y1; later years auto-derive when they do.
          const life = Number(merged.project_life_years) || 1;
          if (life > 0 && (!merged.volumes || merged.volumes.length !== life)) {
            const reshaped = [];
            for (let i = 1; i <= life; i++) {
              const existing = (merged.volumes || []).find((v) => Number(v.year_number) === i);
              reshaped.push(existing || {
                year_number: i,
                year_label: "",
                annual_volume_pcs: 0,
                monthly_volume_pcs: 0,
              });
            }
            merged.volumes = reshaped;
          }
          setFormData(merged);
          baselineRef.current = JSON.stringify(merged);
          // Sync the YoY toggle from the loaded record after data lands —
          // a useEffect keyed on [id] would race the async setFormData.
          setShowYoy(
            Number(merged.yoy_reduction_pct || 0) > 0 ||
            (merged.yoy_apply_years != null && Number(merged.yoy_apply_years) > 0)
          );
          // load rounds
          const rr = await api.get(`/npd/${id}/rounds`);
          setRounds(rr.data.rounds || []);
        } catch (e) {
          toast.error("Linked NPD was deleted or you don't have access");
          nav("/npd");
        } finally { setLoading(false); }
      })();
    }
    // eslint-disable-next-line
  }, [id]);

  // Auto-calc gross weight unless overridden — form-aware
  useEffect(() => {
    if (formData.gross_weight_overridden) return;
    const gw = computeAutoGrossWeight(formData, density);
    if (gw !== formData.gross_weight) {
      setFormData((p) => ({ ...p, gross_weight: gw }));
    }
    // eslint-disable-next-line
  }, [
    formData.stock_form,
    formData.gross_weight_method,
    formData.strip_width, formData.pitch, formData.thickness_mm,
    formData.sheet_width_mm, formData.sheet_length_mm,
    formData.rod_diameter_mm, formData.rod_length_mm, formData.rod_stock_length_mm, formData.rod_end_loss_mm,
    formData.pipe_od_mm, formData.pipe_id_mm, formData.pipe_length_mm,
    formData.bar_side_mm, formData.bar_length_mm,
    formData.hex_across_flats_mm, formData.hex_length_mm,
    density, formData.gross_weight_overridden,
  ]);

  // Round 19.6 — Re-hydrate density from saved material on form load.
  // Without this, opening an existing NPD with a non-steel material falls back to 7.85
  // until the user manually re-picks from the dropdown.
  useEffect(() => {
    if (!formData.material_id || !materials || materials.length === 0) return;
    const m = materials.find((x) => x.material_id === formData.material_id);
    if (m && m.density_g_cc) {
      setDensity(Number(m.density_g_cc));
    }
  }, [formData.material_id, materials]);

  // YoY toggle sync now happens inside the load handler above — keeps it free
  // of the [id]-only race the standalone useEffect had (formData was still
  // INITIAL when the effect ran).

  const STOCK_FORMS = [
    { value: "sheet_strip", label: "Sheet / Strip", scrap: 0.9, hint: "Stamped parts from strip stock" },
    { value: "round_rod",   label: "Round Rod",     scrap: 0.6, hint: "Turned shafts, pins, bolts" },
    { value: "pipe",        label: "Pipe / Tube",   scrap: 0.6, hint: "Hollow shafts, bushings" },
    { value: "square_bar",  label: "Square Bar",    scrap: 0.6, hint: "Machined blocks" },
    { value: "hex_bar",     label: "Hex Bar",       scrap: 0.6, hint: "Bolt heads, nuts, fittings" },
  ];

  const changeStockForm = (newForm) => {
    const def = STOCK_FORMS.find((f) => f.value === newForm);
    setFormData((p) => ({
      ...p,
      stock_form: newForm,
      scrap_recovery_factor: def ? def.scrap : p.scrap_recovery_factor,
    }));
  };

  // Project Scope handlers — year_label is blank until user types Y1's SOP year.
  const updateProjectLife = (years) => {
    const y = Math.max(1, Math.min(20, Math.round(Number(years) || 1)));
    const currentVolumes = formData.volumes || [];
    const newVolumes = [];
    for (let i = 1; i <= y; i++) {
      const existing = currentVolumes.find((v) => Number(v.year_number) === i);
      newVolumes.push(existing || { year_number: i, year_label: "", annual_volume_pcs: 0, monthly_volume_pcs: 0 });
    }
    // If Y1 has a parseable SOP year, fill any still-blank later years.
    const sop = parseInt(String(newVolumes[0]?.year_label || "").trim(), 10);
    if (Number.isFinite(sop) && sop >= 1900 && sop <= 2200) {
      for (let i = 1; i < newVolumes.length; i++) {
        if (!newVolumes[i].year_label) newVolumes[i] = { ...newVolumes[i], year_label: String(sop + i) };
      }
    }
    setFormData((p) => ({ ...p, project_life_years: y, volumes: newVolumes }));
  };

  // When user fills Y1's year_label with a parseable year, auto-derive Y2..Yn
  // only into rows the user hasn't already touched. Manual overrides are preserved.
  const updateVolumeRow = (idx, patch) => {
    setFormData((p) => {
      const rows = (p.volumes || []).map((v, i) => {
        if (i !== idx) return v;
        const updated = { ...v, ...patch };
        if ("annual_volume_pcs" in patch) {
          updated.monthly_volume_pcs = Math.round(((patch.annual_volume_pcs || 0) / 12) * 100) / 100;
        } else if ("monthly_volume_pcs" in patch) {
          updated.annual_volume_pcs = Math.round((patch.monthly_volume_pcs || 0) * 12 * 100) / 100;
        }
        return updated;
      });
      if (idx === 0 && "year_label" in patch) {
        const sop = parseInt(String(patch.year_label).trim(), 10);
        if (Number.isFinite(sop) && sop >= 1900 && sop <= 2200) {
          for (let i = 1; i < rows.length; i++) {
            if (!rows[i].year_label) {
              rows[i] = { ...rows[i], year_label: String(sop + i) };
            }
          }
        }
      }
      return { ...p, volumes: rows };
    });
  };

  const calc = useMemo(() => computeNpd(formData), [formData]);
  const threshold = Number(settings.margin_red_flag_threshold_pct || 15);

  // R54 — per-year YoY reduction helpers. Each year's box defaults to the global rate
  // (within the cap window), so leaving every box untouched keeps the current behaviour.
  const yoyDefaultRate = (yr) => {
    if (yr < 2) return 0;
    const cap = formData.yoy_apply_years && Number(formData.yoy_apply_years) > 0 ? Number(formData.yoy_apply_years) : null;
    return cap === null || yr <= cap + 1 ? (Number(formData.yoy_reduction_pct) || 0) : 0;
  };
  const yoyPerYearVal = (yr) => {
    const m = formData.yoy_per_year_pct || {};
    const v = m[String(yr)];
    return v === undefined || v === null || v === "" ? yoyDefaultRate(yr) : v;
  };
  const setYoyPerYear = (yr, val) => {
    setFormData((p) => {
      const m = { ...(p.yoy_per_year_pct || {}) };
      if (val === "" || val === null || val === undefined) delete m[String(yr)];
      else m[String(yr)] = Number(val);
      return { ...p, yoy_per_year_pct: m };
    });
  };
  // Per-year price from the single source of truth (calc.yoy_schedule), so every table
  // shows the same number as the schedule / preview / Excel.
  const yoyPriceFor = (yr) => {
    const r = (calc.yoy_schedule || []).find((s) => s.year_number === yr);
    return r ? r.price_per_pc : (calc.grand_total || 0);
  };

  // Same-unit users can edit (matches backend: everyone in a unit works on that unit's costings).
  const sameUnit = !!(user?.unit_id && formData.unit_id && formData.unit_id === user.unit_id);
  const canEdit = npdMeta ? (canEditAll() || sameUnit || formData.assigned_to === user.user_id) : true;

  const updateField = (name, value) => setFormData((p) => ({ ...p, [name]: value }));

  // Other One-time Development Cost — fixed category list (no DB setting).
  // Hardcoded so the dropdown never depends on an app_settings row that may be
  // missing on a fresh install, a restored backup, or a different environment.
  const devCategories = ["Testing", "Machinery", "Other"];
  const addOtherDevRow = () =>
    setFormData((p) => ({
      ...p,
      other_dev_costs: [...(p.other_dev_costs || []), { category: devCategories[0] || "Other", amount: 0, remarks: "" }],
    }));
  const updateOtherDevRow = (idx, key, value) =>
    setFormData((p) => {
      const rows = [...(p.other_dev_costs || [])];
      rows[idx] = { ...rows[idx], [key]: value };
      return { ...p, other_dev_costs: rows };
    });
  const removeOtherDevRow = (idx) =>
    setFormData((p) => ({
      ...p,
      other_dev_costs: (p.other_dev_costs || []).filter((_, i) => i !== idx),
    }));
  const otherDevTotal = (formData.other_dev_costs || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);

  // RMC-completeness gate — blocks finalize actions (Approve/Won/Round/Preview/Export).
  const rmcReasons = useMemo(() => rmcIncompleteReasons(formData), [formData]);
  const rmcBlocked = rmcReasons.length > 0;
  const rmcTitle = rmcBlocked ? "Complete Raw Material section first — Net RMC missing" : undefined;
  const apiErr = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (d && typeof d === "object") {
      return `${d.message || fallback}${Array.isArray(d.reasons) && d.reasons.length ? " — " + d.reasons.join("; ") : ""}`;
    }
    return (typeof d === "string" && d) || fallback;
  };

  const onMaterialSelect = (m) => {
    setDensity(m.density_g_cc || 7.85);
    setFormData((p) => ({
      ...p,
      material_id: m.material_id,
      material_grade: m.grade,
      rm_price_per_kg: m.default_rm_rate_per_kg || p.rm_price_per_kg,
      scrap_price_per_kg: m.default_scrap_rate_per_kg || p.scrap_price_per_kg,
    }));
  };

  const overrideGrossWeight = (val) => setFormData((p) => ({ ...p, gross_weight: val, gross_weight_overridden: true }));
  const resetGrossWeight = () => setFormData((p) => ({ ...p, gross_weight_overridden: false }));

  const save = async (silent = false) => {
    // No-op if nothing changed on an existing draft — don't re-save; just tell the user.
    if (!silent && isEdit && baselineRef.current != null && !isDirty()) {
      toast("No changes to save");
      return false;
    }
    // Collect every blocker so one popup lists exactly what to fix (not a toast at a time).
    // Mandatory fields are always enforced — no bypass.
    const bypassMandatory = false;
    const errors = [];
    // Data-sanity (kept even when mandatory fields are bypassed):
    {
      const gw = Number(formData.gross_weight) || 0;
      const nw = Number(formData.net_weight) || 0;
      if (gw > 0 && nw > gw) errors.push(`Net Weight (${nw} kg) cannot exceed Gross Weight (${gw} kg).`);
    }
    if (!bypassMandatory) {
      if (!formData.customer_id) errors.push("Select a customer.");
      if (!formData.part_number || !formData.part_name) errors.push("Part Number and Part Name are required.");
      if (!formData.category) errors.push("Select Category (2W / 4W).");
      if (formData.category === "Other" && !(formData.category_other || "").trim()) errors.push("Specify the 'Other' category.");
      if (!isEdit && user?.role === "admin" && !formData.unit_id) errors.push("Pick a unit for this NPD.");
      // Cycle time AND tonnage are mandatory for every machine process step (No-machine steps exempt).
      const missingCT = (formData.processes || []).filter((p) => !p.no_machine && !(Number(p.cycle_time_sec) > 0));
      if (missingCT.length) errors.push(`Cycle time required for: ${missingCT.map((p) => p.name || "(unnamed)").join(", ")}.`);
      const missingTon = (formData.processes || []).filter((p) => !p.no_machine && !(p.tonnage && String(p.tonnage).trim()));
      if (missingTon.length) errors.push(`Tonnage required for: ${missingTon.map((p) => p.name || "(unnamed)").join(", ")}.`);
      // Low monthly RM gate — MOQ + supplier confirmation, or "covered by siblings" / "RM by customer".
      const material = materials.find((m) => m.material_id === formData.material_id);
      const d = deriveMonthlyRmBlock(formData, material);
      const moqGateBlocked = d.low_volume_flag
        && !(formData.project_id && formData.moq_covered_by_siblings)
        && !formData.rm_provided_by_customer
        && (!(Number(formData.moq_kg) > 0) || !((formData.moq_confirmed_with || "").trim()));
      if (moqGateBlocked) errors.push("Low monthly RM: capture MOQ (kg) + supplier confirmation, or tick 'MOQ covered by other parts' or 'RM will be provided by Customer'.");
    }
    if (errors.length) {
      if (silent) toast.error(errors[0]);
      else toast.error("Draft not saved — fix these", { description: errors.join(" · ") });
      return false;
    }
    setSaving(true);
    try {
      // Selling Price is auto-derived from Grand Total in the new model
      const payload = { ...formData, selling_price: calc.grand_total };
      if (isEdit) {
        const wasApproved = !!npdMeta?.approved_for_export;
        const r = await api.put(`/npd/${id}`, payload);
        // Persisted → this is the new clean baseline (no unsaved edits).
        baselineRef.current = JSON.stringify(formData);
        if (wasApproved) {
          // R53 — any edit demotes an approved quote back to draft. Reflect it so
          // the Approve control reappears and the Excel button hides.
          setNpdMeta((p) => (p ? { ...p, approved_for_export: false } : p));
          if (!silent) toast.success("✓ Draft saved", { description: "Approval revoked — a manager must re-approve before the Excel download." });
        } else {
          if (!silent) toast.success("✓ Draft saved", { description: `${npdMeta?.npd_number || "This costing"} saved as a draft.` });
        }
        // This part is a component of one or more assemblies → they were sent back for re-approval.
        const rev = r?.data?.reverted_assemblies || [];
        if (!silent && rev.length) {
          toast.warning(
            rev.length === 1 ? "Linked assembly sent for re-approval" : `${rev.length} linked assemblies sent for re-approval`,
            { description: `${rev.map((a) => a.assembly_number).join(", ")} moved to Under Approval because this child part changed.` }
          );
        }
      } else {
        const r = await api.post("/npd", payload);
        if (!silent) toast.success("✓ Draft saved", { description: `Created ${r.data.npd_number} as a draft.` });
        nav(`/npd/${r.data.npd_id}`, { replace: true });
      }
      return true;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(Array.isArray(msg) ? "Draft not saved" : "Draft not saved", { description: Array.isArray(msg) ? msg.join(" · ") : msg });
      return false;
    } finally { setSaving(false); }
  };

  const saveRound = async () => {
    // Round 43 — Two-step atomic action: (1) save the form data to the npd doc,
    // (2) snapshot the now-current doc as a new round. Without step (1) the
    // round would snapshot stale DB state and silently discard form edits.
    if (!isEdit) {
      toast.error("Save a draft first (Draft button) before you can Save a round.");
      return;
    }
    if (!formData.customer_id) { toast.error("Please select a customer"); return; }
    if (!formData.part_number || !formData.part_name) { toast.error("Part Number and Part Name are required"); return; }
    if (!formData.category) { toast.error("Please select Category (2W / 4W)"); return; }
    {
      const gw = Number(formData.gross_weight) || 0;
      const nw = Number(formData.net_weight) || 0;
      if (gw > 0 && nw > gw) {
        toast.error(`Net Weight (${nw} kg) cannot be more than Gross Weight (${gw} kg).`);
        return;
      }
    }
    {
      const missingCT = (formData.processes || []).filter((p) => !p.no_machine && !(Number(p.cycle_time_sec) > 0));
      if (missingCT.length) {
        toast.error(`Cycle time is required for process step(s): ${missingCT.map((p) => p.name || "(unnamed)").join(", ")}.`);
        return;
      }
      const missingTon = (formData.processes || []).filter((p) => !p.no_machine && !(p.tonnage && String(p.tonnage).trim()));
      if (missingTon.length) {
        toast.error(`Tonnage is required for process step(s): ${missingTon.map((p) => p.name || "(unnamed)").join(", ")}.`);
        return;
      }
    }

    setSaving(true);
    try {
      // Step 1 — Persist current form data first
      const payload = { ...formData, selling_price: calc.grand_total };
      await api.put(`/npd/${id}`, payload);

      // Step 2 — Snapshot the just-saved doc as the next round (auto N1, N2, …). No prompt.
      const r = await api.post(`/npd/${id}/save-round`, { notes: "" });
      if (r.data?.approval_revoked) {
        toast.success(`Round ${r.data.round_label} saved. Previous approval revoked — needs re-approval for export.`);
      } else {
        toast.success(`Round ${r.data?.round_label || ""} saved`);
      }

      const rr = await api.get(`/npd/${id}/rounds`);
      setRounds(rr.data.rounds || []);
      const fresh = await api.get(`/npd/${id}`);
      setNpdMeta({
        npd_number: fresh.data.npd_number, status: fresh.data.status,
        current_round: fresh.data.current_round, approved_for_export: fresh.data.approved_for_export,
        is_awarded: fresh.data.is_awarded, awarded_at: fresh.data.awarded_at, awarded_by: fresh.data.awarded_by,
        outcome: fresh.data.outcome || "pending", outcome_at: fresh.data.outcome_at, outcome_by: fresh.data.outcome_by,
      });
    } catch (e) {
      toast.error(apiErr(e, "Failed to save round"));
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    const isApproving = !npdMeta?.approved_for_export;
    if (!window.confirm(isApproving ? "Save changes as a new round and approve this NPD for Excel export?" : "Revoke approval?")) return;
    try {
      // Approve = SAVE the current edits + record them as the next round (N1, N2, …), then
      // approve the freshly-saved version. Restores the earlier "Save round" behaviour so a
      // new round shows up whenever the quote changes, and the approved quote matches the screen.
      if (isApproving && isEdit) {
        const ok = await save(true); // silent — the single combined toast is shown below
        if (!ok) return; // validation/save failed — don't approve stale data
        await api.post(`/npd/${id}/save-round`, { notes: "" });
        const list = await api.get(`/npd/${id}/rounds`);
        setRounds(list.data.rounds || []);
        const fresh = await api.get(`/npd/${id}`);
        setNpdMeta((p) => (p ? { ...p, current_round: fresh.data.current_round } : p));
      }
      const r = await api.post(`/npd/${id}/approve`, { approved: isApproving });
      setNpdMeta((p) => ({
        ...p, approved_for_export: r.data.approved_for_export,
        approved_at: r.data.approved_for_export ? (r.data.approved_at || new Date().toISOString()) : null,
        approved_by_name: r.data.approved_for_export ? (r.data.approved_by_name || user?.name || p?.approved_by_name) : null,
      }));
      toast.success(r.data.approved_for_export ? "Saved & approved for export ✓" : "Approval revoked");
    } catch (e) { toast.error(apiErr(e, "Approval failed")); }
  };

  const setOutcome = async (newOutcome) => {
    if ((npdMeta?.outcome || "pending") === newOutcome) return;
    const confirmMsg =
      newOutcome === "won"
        ? `Mark ${npdMeta?.npd_number} as WON? This moves it into the awarded business quantum and flips the linked RFQ to Won.${!npdMeta?.approved_for_export ? "\n\nNote: this NPD is not yet Approved for Export." : ""}`
        : newOutcome === "lost"
        ? `Mark ${npdMeta?.npd_number} as LOST? This flips the linked RFQ to Lost too. Customer chose a different supplier.`
        : newOutcome === "dropped"
        ? `Mark ${npdMeta?.npd_number} as DROPPED? This flips the linked RFQ to Dropped too. We chose not to pursue, or customer abandoned.`
        : `Reopen ${npdMeta?.npd_number} (set back to Pending)? The linked RFQ will be reopened too.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await api.post(`/npd/${id}/set-outcome`, { outcome: newOutcome });
      setNpdMeta((p) => ({
        ...p,
        outcome: r.data.outcome,
        outcome_at: r.data.outcome_at,
        outcome_by: r.data.outcome_by,
        is_awarded: r.data.is_awarded,
        awarded_at: r.data.is_awarded ? r.data.outcome_at : null,
        awarded_by: r.data.is_awarded ? r.data.outcome_by : null,
      }));
      toast.success(newOutcome === "pending" ? "Reopened" : `Marked ${newOutcome.toUpperCase()}`);
    } catch (e) {
      toast.error(apiErr(e, "Update failed"));
    }
  };

  const exportExcel = async () => {
    // The Excel is built server-side from the SAVED record. If the form has unsaved
    // edits (e.g. changed YoY / SOP year / volumes), the download would show old data —
    // so persist first. Saving revokes approval (R53), so the user must re-approve.
    if (isDirty()) {
      const doSave = window.confirm(
        "You have unsaved changes that won't appear in the downloaded Excel.\n\n" +
        "Click OK to SAVE them now — a manager must then re-approve before you can download the updated quote.\n\n" +
        "Click Cancel to download the currently approved (saved) version as-is."
      );
      if (doSave) {
        const ok = await save();
        if (ok) toast.success("Changes saved. Ask a manager to re-approve, then click Excel to download the updated quote.");
        return; // approval revoked on save → a fresh download isn't available until re-approval
      }
      // Cancel → fall through and download the currently approved/saved version.
    }
    try {
      const res = await api.get(`/npd/${id}/export.xlsx?t=${Date.now()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${npdMeta?.npd_number || "NPD"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed. (Available after Stage 6)");
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Loading…</div>;

  return (
    <div className="p-4 lg:p-6 max-w-[1400px] mx-auto" data-testid="npd-form-page">
      {isEdit && formData.project_id && (
        <div className="mb-3 text-xs text-slate-500 flex items-center gap-1.5" data-testid="npd-project-breadcrumb">
          <Briefcase size={12} />
          <span>Part of</span>
          <Link
            to={`/projects/${formData.project_id}`}
            className="text-orange-600 hover:text-orange-700 font-mono font-medium"
          >
            {npdMeta?.npd_number?.split("/")[0] || formData.npd_number?.split("/")[0]}
          </Link>
          {formData.child_index && (
            <span className="font-mono">· P{formData.child_index}</span>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button type="button" onClick={goBack} className="p-2 hover:bg-slate-100 rounded-md text-slate-500" data-testid="npd-back-btn"><ArrowLeft size={18} /></button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">{isEdit ? (formData.part_name || "NPD") : "New NPD Costing"}</h1>
              {npdMeta?.approved_for_export && (
                <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded inline-flex items-center gap-1"
                  title={npdMeta.approved_by_name ? `Approved by ${npdMeta.approved_by_name}` : "Approved"}>
                  <CheckCircle2 size={12} /> Approved{npdMeta.approved_at ? ` · ${new Date(npdMeta.approved_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}` : ""}
                </span>
              )}
              {npdMeta?.outcome === "won" && (
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded inline-flex items-center gap-1" data-testid="outcome-badge-won">
                  <Award size={12} /> Won
                </span>
              )}
              {npdMeta?.outcome === "lost" && (
                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded" data-testid="outcome-badge-lost">
                  Lost
                </span>
              )}
              {npdMeta?.outcome === "dropped" && (
                <span className="text-xs bg-stone-200 text-stone-800 px-2 py-0.5 rounded" data-testid="outcome-badge-dropped">
                  Dropped
                </span>
              )}
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-2">
              {npdMeta && <span className="font-mono">{npdMeta.npd_number}</span>}
              {npdMeta && <span>• Status: <strong className="uppercase">{npdMeta.status}</strong></span>}
              {npdMeta && npdMeta.current_round > 0 && <span>• Round {npdMeta.current_round}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEdit && (
            <>
              {canEdit && (
                <button
                  onClick={() => save()}
                  disabled={saving}
                  className={`px-4 py-2 ${isDirty() ? "bg-sky-700 hover:bg-sky-800" : "bg-sky-500 hover:bg-sky-600"} disabled:bg-sky-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md flex items-center gap-1.5`}
                  data-testid="npd-save"
                  title={isDirty() ? "You have unsaved changes — click to save the draft" : "No unsaved changes (viewing). Edit something to enable saving changes."}
                >
                  <Save size={14} /> {saving ? "Saving…" : "Draft"}
                </button>
              )}
              {canApprove && (() => {
                // R31 — Client-side mirror of the backend approve gate. Prevents
                // a confusing 400 on the server roundtrip.
                const material = materials.find((m) => m.material_id === formData.material_id);
                const d = deriveMonthlyRmBlock(formData, material);
                const moqGateBlocked =
                  d.low_volume_flag &&
                  !(formData.project_id && formData.moq_covered_by_siblings) &&
                  !formData.rm_provided_by_customer &&
                  (!(Number(formData.moq_kg) > 0) || !((formData.moq_confirmed_with || "").trim()));
                const alreadyApproved = !!npdMeta?.approved_for_export;
                // When already approved, allow Revoke regardless of gate. Mandatory fields are
                // always enforced, so the approve gate always applies.
                const disabled = !alreadyApproved && (moqGateBlocked || rmcBlocked);
                return (
                  <button
                    onClick={approve}
                    disabled={disabled}
                    title={disabled ? (rmcBlocked ? rmcTitle : "Low monthly RM — fill MOQ + supplier confirmation in Section A → Sourcing block") : ""}
                    className={`px-3 py-2 text-sm rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${alreadyApproved ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "border border-emerald-500 text-emerald-700 hover:bg-emerald-50"}`}
                    data-testid="approve-btn"
                  >
                    {alreadyApproved ? "✓ Approved" : "Approve"}
                  </button>
                );
              })()}
              {canAward && (() => {
                const o = npdMeta?.outcome || "pending";
                return (
                  <div className="inline-flex items-stretch rounded-md overflow-hidden border border-slate-300" data-testid="outcome-btn-group">
                    <button type="button" onClick={() => setOutcome("won")} disabled={rmcBlocked} title={rmcBlocked ? rmcTitle : "Mark as Won — we got this business"} className={`px-2.5 py-2 text-xs font-medium flex items-center gap-1 border-r border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed ${o === "won" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700 hover:bg-emerald-50"}`} data-testid="outcome-won-btn">
                      <Award size={12} /> Won
                    </button>
                    <button type="button" onClick={() => setOutcome("lost")} className={`px-2.5 py-2 text-xs font-medium border-r border-slate-300 ${o === "lost" ? "bg-red-600 text-white" : "bg-white text-red-700 hover:bg-red-50"}`} title="Mark as Lost — customer chose another supplier" data-testid="outcome-lost-btn">
                      Lost
                    </button>
                    <button type="button" onClick={() => setOutcome("dropped")} className={`px-2.5 py-2 text-xs font-medium ${o === "dropped" ? "bg-stone-600 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`} title="Mark as Dropped — we chose not to pursue, or customer abandoned" data-testid="outcome-dropped-btn">
                      Dropped
                    </button>
                    {o !== "pending" && (
                      <button type="button" onClick={() => setOutcome("pending")} className="px-2.5 py-2 text-xs font-medium border-l border-slate-300 bg-white text-slate-600 hover:bg-slate-100" title="Reopen — set back to Pending" data-testid="outcome-reopen-btn">
                        ↺ Reopen
                      </button>
                    )}
                  </div>
                );
              })()}
              {npdMeta?.approved_for_export && (
                <button onClick={exportExcel} disabled={rmcBlocked} title={rmcTitle} className="px-3 py-2 text-sm bg-slate-900 hover:bg-slate-800 text-white rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="export-xlsx-btn">
                  <Download size={14} /> Excel
                </button>
              )}
              <button
                onClick={() => window.open(`/npd/${id}/preview`, "_blank")}
                disabled={rmcBlocked}
                className="px-3 py-2 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-md flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="preview-btn"
                title={rmcBlocked ? rmcTitle : "Open quote preview in a new tab"}
              >
                <Eye size={14} /> Preview
              </button>
            </>
          )}
          {/* Draft button for a brand-new (unsaved) costing — the finalize row above is edit-only. */}
          {!isEdit && canEdit && (
            <button
              onClick={() => save()}
              disabled={saving}
              className="px-4 py-2 bg-sky-600 hover:bg-sky-700 disabled:bg-sky-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
              data-testid="npd-save"
              title="Save your progress as a draft (does not create a round)"
            >
              <Save size={14} /> {saving ? "Saving…" : "Draft"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-4">
        <div className="space-y-4 min-w-0">
          {/* R29 — Save-gate banner: drawing file, screenshot AND RFQ mail are mandatory on existing NPDs. */}
          {isEdit && canEdit && (!formData.drawing_file_path || !formData.drawing_screenshot_path || (!formData.project_id && !formData.rfq_mail_path)) && (
            <div
              className="bg-amber-50 border border-amber-300 rounded-md p-3 flex items-start gap-2 text-sm text-amber-900"
              data-testid="drawing-required-banner"
            >
              <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <strong>Drawing, Screenshot &amp; RFQ Mail are required to Save this record.</strong>{" "}
                Upload the missing item(s) in the sections below:
                <ul className="list-disc ml-5 mt-1">
                  {!formData.drawing_file_path && <li>Source drawing (PDF/JPG/PNG/TIF) — <em>Drawing</em> section.</li>}
                  {!formData.drawing_screenshot_path && <li>Drawing screenshot (JPG/PNG) — <em>Drawing Screenshot</em> section.</li>}
                  {!formData.project_id && !formData.rfq_mail_path && <li>RFQ mail (PDF/MSG/EML/…) — <em>RFQ Mail</em> section.</li>}
                </ul>
              </div>
            </div>
          )}
          {!isEdit && cloneSources.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="clone-from-block">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="text-sm font-semibold text-orange-900">Start from existing costing</h3>
                  <p className="text-xs text-orange-700/80 mt-0.5">Pick a past NPD as a template. Everything except drawing, approval, and rounds will be copied. You'll land on the new record to edit.</p>
                </div>
                <div className="relative min-w-[280px]" data-testid="clone-source-search">
                  <input
                    type="text"
                    value={cloneQuery}
                    onChange={(e) => { setCloneQuery(e.target.value); setCloneOpen(true); }}
                    onFocus={() => setCloneOpen(true)}
                    onBlur={() => setTimeout(() => setCloneOpen(false), 150)}
                    placeholder="Search part name, number, or NPD #…"
                    className="w-full px-3 py-2 border border-orange-300 bg-white rounded-md text-sm"
                    data-testid="clone-source-input"
                  />
                  {cloneOpen && (
                    <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto bg-white border border-orange-200 rounded-md shadow-lg">
                      {(() => {
                        const q = cloneQuery.trim().toLowerCase();
                        const list = q
                          ? cloneSources.filter((n) =>
                              [n.npd_number, n.part_name, n.part_number]
                                .filter(Boolean)
                                .some((f) => String(f).toLowerCase().includes(q)))
                          : cloneSources;
                        if (list.length === 0) {
                          return <div className="px-3 py-2 text-sm text-slate-400">No matches</div>;
                        }
                        return list.slice(0, 100).map((n) => (
                          <button
                            key={n.npd_id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); cloneFromSource(n.npd_id); setCloneOpen(false); setCloneQuery(""); }}
                            className="block w-full text-left px-3 py-2 text-sm hover:bg-orange-50"
                            data-testid="clone-source-option"
                          >
                            {n.npd_number} — {n.part_name || "(no name)"} {n.part_number ? `· ${n.part_number}` : ""}
                          </button>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <Section title="Part Identity">
            <Field label="Customer *">
              <select value={formData.customer_id} onChange={(e) => updateField("customer_id", e.target.value)} className="input" data-testid="npd-customer" disabled={!canEdit}>
                <option value="">Select customer…</option>
                {customers.map((c) => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
              </select>
            </Field>
            {user?.role === "admin" && (
              <Field label={isEdit ? "Unit (admin can reassign)" : "Unit *"}>
                <select value={formData.unit_id || ""} onChange={(e) => updateField("unit_id", e.target.value)}
                        className="input" data-testid="npd-unit">
                  <option value="">Select unit…</option>
                  {units.map((u) => <option key={u.unit_id} value={u.unit_id}>{u.unit_code} — {u.unit_name}</option>)}
                </select>
                {isEdit && (
                  <div className="text-[11px] text-slate-500 mt-1">Changing the unit does NOT renumber the NPD. The number prefix (e.g. DEF-NPD-…) stays as a historical label. Only unit-based dashboard scoping and RBAC are affected.</div>
                )}
              </Field>
            )}
            <Field label="End Customer (optional)">
              <input value={formData.end_customer || ""} onChange={(e) => updateField("end_customer", e.target.value)} className="input" placeholder="e.g. AISIN" disabled={!canEdit} />
            </Field>
            <Field label="Part Number *">
              <input required value={formData.part_number} onChange={(e) => updateField("part_number", e.target.value)} className="input" data-testid="npd-part-number" disabled={!canEdit} />
              {dupCheck.matches.length > 0 && (
                <div className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="npd-dup-warning">
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                  <div>
                    Heads-up: part number "<span className="font-mono">{formData.part_number}</span>" already exists on{" "}
                    {dupCheck.matches.slice(0, 3).map((m, i) => (
                      <span key={m.npd_id}>
                        {i > 0 && ", "}
                        <Link to={`/npd/${m.npd_id}`} className="underline hover:text-amber-900">{m.npd_number}</Link>
                        {m.customer_name && <span className="text-amber-700"> ({m.customer_name})</span>}
                      </span>
                    ))}
                    {dupCheck.matches.length > 3 && ` (+${dupCheck.matches.length - 3} more)`}. Save anyway if this is intentional.
                  </div>
                </div>
              )}
            </Field>
            <Field label="Part Name *"><input required value={formData.part_name} onChange={(e) => updateField("part_name", e.target.value)} className="input" data-testid="npd-part-name" disabled={!canEdit} /></Field>
            <Field label="Drawing Number"><input value={formData.drawing_number || ""} onChange={(e) => updateField("drawing_number", e.target.value)} className="input" disabled={!canEdit} /></Field>
            <Field label="Model Name">
              <input
                value={formData.model_name || ""}
                onChange={(e) => updateField("model_name", e.target.value)}
                className="input"
                placeholder="e.g. Honda City, Maruti Ertiga"
                disabled={!canEdit}
                data-testid="npd-model-name"
              />
            </Field>
            <Field label="Application">
              <input
                value={formData.application || ""}
                onChange={(e) => updateField("application", e.target.value)}
                className="input"
                placeholder="e.g. Brake caliper bracket"
                disabled={!canEdit}
                data-testid="npd-application"
              />
            </Field>
            <Field label="Customer Due Date"><input type="date" value={formData.customer_due_date || ""} onChange={(e) => updateField("customer_due_date", e.target.value)} className="input" disabled={!canEdit} /></Field>
            <Field label="Category (2W / 4W) *">
              <div className={`w-full flex items-stretch border border-slate-300 rounded-md bg-white overflow-hidden focus-within:ring-2 focus-within:ring-orange-500 ${!canEdit ? "opacity-60" : ""}`}>
                <select
                  required
                  value={formData.category || ""}
                  onChange={(e) => updateField("category", e.target.value)}
                  className={`bg-transparent border-0 focus:ring-0 focus:outline-none text-sm py-2 pl-3 pr-1 ${formData.category === "Other" ? "w-24 shrink-0" : "w-full"}`}
                  disabled={!canEdit}
                  data-testid="npd-category"
                >
                  <option value="">—</option>
                  <option value="2W">2W (2-Wheeler)</option>
                  <option value="4W">4W (4-Wheeler)</option>
                  <option value="Other">Other</option>
                </select>
                {formData.category === "Other" && (
                  <input
                    type="text"
                    value={formData.category_other || ""}
                    onChange={(e) => updateField("category_other", e.target.value)}
                    className="flex-1 min-w-0 border-0 border-l border-slate-200 bg-transparent focus:ring-0 focus:outline-none text-sm py-2 px-2"
                    placeholder="specify (e.g. 3-Wheeler, CV, Off-road)"
                    disabled={!canEdit}
                    data-testid="npd-category-other"
                  />
                )}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">Auto-fills the APQP Category when this is Won.</div>
            </Field>
          </Section>

          {!isProto && (
          <Section
            title="Project Scope"
            subtitle="How many years is the part expected to run, and what are the customer-projected annual volumes? YoY price reduction (if any) is set lower down — it's optional."
          >
            <Field label="Project Life (years)">
              <input
                type="number" step="1" min="1" max="20"
                value={(formData.project_life_years ?? 1) === 0 || (formData.project_life_years ?? 1) === "0" ? "" : (formData.project_life_years ?? 1)}
                onChange={(e) => updateProjectLife(parseInt(e.target.value) || 1)}
                className="input"
                placeholder="1"
                data-testid="project-life-years"
                disabled={!canEdit}
              />
              <div className="text-[11px] text-slate-500 mt-1">Even one-off parts get a 1-year schedule. The volumes table renders below.</div>
            </Field>
            <Field label="SOP Year (Y1 calendar)">
              <input
                value={(formData.volumes && formData.volumes[0] && formData.volumes[0].year_label) || ""}
                onChange={(e) => updateVolumeRow(0, { year_label: e.target.value })}
                className="input"
                placeholder="e.g. 2027"
                disabled={!canEdit}
                data-testid="sop-year"
              />
              <div className="text-[11px] text-slate-500 mt-1">Type the year SOP begins (e.g. 2027). Y2..Yn auto-fill if you leave them blank.</div>
            </Field>

            <div className="col-span-2 mt-2">
              <div className="text-xs font-medium text-slate-600 mb-2">Year-by-Year Schedule</div>
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden" data-testid="project-volumes-table">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-16">Year</th>
                    <th className="text-left p-2 w-24">Calendar</th>
                    <th className="text-right p-2">Annual Vol (pcs)</th>
                    <th className="text-right p-2">Monthly Vol</th>
                    <th className="text-right p-2">Price/pc{showYoy && Number(formData.yoy_reduction_pct || 0) > 0 ? " (YoY)" : ""}</th>
                    <th className="text-right p-2">Annual Value</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(formData.volumes || []).map((v, idx) => {
                    const price = yoyPriceFor(v.year_number);
                    const value = price * (v.annual_volume_pcs || 0);
                    return (
                      <tr key={v.year_number}>
                        <td className="p-2 font-mono text-slate-500">Y{v.year_number}</td>
                        <td className="p-2">
                          <input
                            value={v.year_label || ""}
                            onChange={(e) => updateVolumeRow(idx, { year_label: e.target.value })}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                            placeholder={idx === 0 ? "SOP yr" : "—"}
                            disabled={!canEdit}
                            data-testid={`year-label-${v.year_number}`}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number" step="1" min="0"
                            value={(v.annual_volume_pcs || 0) === 0 || (v.annual_volume_pcs || 0) === "0" ? "" : (v.annual_volume_pcs || 0)}
                            onChange={(e) => updateVolumeRow(idx, { annual_volume_pcs: parseFloat(e.target.value) || 0 })}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                            placeholder="0"
                            disabled={!canEdit}
                            data-testid={`annual-vol-${v.year_number}`}
                          />
                        </td>
                        <td className="p-2">
                          <input
                            type="number" step="1" min="0"
                            value={(v.monthly_volume_pcs || 0) === 0 || (v.monthly_volume_pcs || 0) === "0" ? "" : (v.monthly_volume_pcs || 0)}
                            onChange={(e) => updateVolumeRow(idx, { monthly_volume_pcs: parseFloat(e.target.value) || 0 })}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                            placeholder="0"
                            disabled={!canEdit}
                            data-testid={`monthly-vol-${v.year_number}`}
                          />
                        </td>
                        <td className="p-2 text-right font-mono">{formatINR(price)}</td>
                        <td className="p-2 text-right font-mono font-semibold">{formatINR(value)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-100">
                  <tr>
                    <td colSpan={5} className="p-2 font-semibold text-right">TOTAL PROJECT VALUE</td>
                    <td className="p-2 text-right font-mono font-bold" data-testid="project-total-value">
                      {formatINR(calc.total_project_value || 0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
              <p className="text-[11px] text-slate-500 mt-2">
                Editing annual auto-fills monthly (÷12); editing monthly auto-fills annual (×12). Price/pc reflects YoY discount if you've set one below.
              </p>
            </div>
          </Section>
          )}

          {isProto && (
          <Section
            title="Project Scope"
            subtitle="Prototype quotation — per-piece quantity for this prototype run."
          >
            <Field label="Project Quantity">
              <input
                type="number" step="1" min="1"
                value={(formData.prototype_qty ?? 1) === 0 || (formData.prototype_qty ?? 1) === "0" ? "" : (formData.prototype_qty ?? 1)}
                onChange={(e) => updateField("prototype_qty", e.target.value === "" ? "" : (parseInt(e.target.value) || 1))}
                className="input"
                disabled={!canEdit}
                data-testid="prototype-qty"
              />
              <div className="text-[11px] text-slate-500 mt-1">Number of prototype pieces for this quotation.</div>
            </Field>
          </Section>
          )}

          <Section id="sec-a" title="A. Raw Material" subtitle={`Stock form: ${STOCK_FORMS.find(f => f.value === formData.stock_form)?.label || "—"}`}>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Stock Form</label>
              <div className="flex flex-wrap gap-1.5">
                {STOCK_FORMS.map((f) => (
                  <button
                    key={f.value}
                    type="button"
                    onClick={() => changeStockForm(f.value)}
                    disabled={!canEdit}
                    className={`px-3 py-1.5 text-xs rounded-md border transition disabled:opacity-50 ${
                      formData.stock_form === f.value
                        ? "bg-orange-600 text-white border-orange-600 font-semibold"
                        : "bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100"
                    }`}
                    title={f.hint}
                    data-testid={`stock-form-${f.value}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">
                Switching form auto-sets scrap recovery factor ({STOCK_FORMS.find(f => f.value === formData.stock_form)?.scrap}). You can override below.
              </p>
            </div>

            <Field label="Material Grade">
              <MaterialGradePicker
                value={formData.material_grade}
                onChange={(g) => updateField("material_grade", g)}
                onMaterialSelect={onMaterialSelect}
              />
            </Field>

            {/* Rate inputs — high-priority, kept right under Material Grade so they're
                never missed during review (they drive the entire RM cost). */}
            <div className="col-span-2 rounded-lg border-2 border-amber-300 bg-amber-50/60 p-3" data-testid="rm-rate-box">
              <div className="text-xs font-bold uppercase tracking-wide text-amber-800 mb-2">Rate inputs · drives RM cost</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">RM Rate (₹/kg)</label>
                  <DecimalInput value={formData.rm_price_per_kg} onChange={(v) => updateField("rm_price_per_kg", v)} className="input font-semibold" data-testid="rm-rate" disabled={!canEdit} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Scrap Rate (₹/kg)</label>
                  <DecimalInput value={formData.scrap_price_per_kg} onChange={(v) => updateField("scrap_price_per_kg", v)} className="input font-semibold" data-testid="scrap-rate" disabled={!canEdit} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Scrap Recovery Factor</label>
                  <DecimalInput value={formData.scrap_recovery_factor} onChange={(v) => updateField("scrap_recovery_factor", v)} className="input font-semibold" disabled={!canEdit} />
                </div>
              </div>
            </div>

            {formData.stock_form === "sheet_strip" && (
              <>
                {/* R44 — Gross weight calc method (sheet vs coil/progressive) */}
                <div className="col-span-2 mt-1 mb-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-slate-500 font-medium">Gross weight method:</span>
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => updateField("gross_weight_method", "sheet")}
                      className={`px-2.5 py-1 rounded border ${
                        (formData.gross_weight_method || "sheet") === "sheet"
                          ? "bg-orange-600 text-white border-orange-600"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                      }`}
                      title="Sheet method: sheet dimensions + utilization. Right for sheet-fed parts. Captures edge-trim waste."
                      data-testid="gw-method-sheet"
                    >
                      Sheet layout
                    </button>
                    <button
                      type="button"
                      disabled={!canEdit}
                      onClick={() => updateField("gross_weight_method", "coil")}
                      className={`px-2.5 py-1 rounded border ${
                        formData.gross_weight_method === "coil"
                          ? "bg-orange-600 text-white border-orange-600"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                      }`}
                      title="Coil/Progressive method: width × pitch × thickness × density. Right for coil-fed progressive parts."
                      data-testid="gw-method-coil"
                    >
                      Coil / Progressive
                    </button>
                    <span className="text-[10px] text-slate-400 ml-1">
                      {formData.gross_weight_method === "coil"
                        ? "= W × pitch × t × ρ / 10⁶"
                        : "= sheet_weight / pcs_per_sheet (captures edge-trim)"}
                    </span>
                  </div>
                </div>

                <Field label="Thickness (mm)"><DecimalInput value={formData.thickness_mm} onChange={(v) => updateField("thickness_mm", v)} className="input" disabled={!canEdit} /></Field>
                <Field label="Strip Width (mm)"><DecimalInput value={formData.strip_width} onChange={(v) => updateField("strip_width", v)} className="input" disabled={!canEdit} /></Field>
                <Field label="Pitch of Feed (mm)"><DecimalInput value={formData.pitch} onChange={(v) => updateField("pitch", v)} className="input" disabled={!canEdit} /></Field>

                {/* Sheet Width / Length only relevant for sheet method */}
                {(formData.gross_weight_method || "sheet") === "sheet" && (
                  <>
                    <Field label="Sheet Width (mm)">
                      <input type="number" step="1" value={(formData.sheet_width_mm ?? 1250) === 0 || (formData.sheet_width_mm ?? 1250) === "0" ? "" : (formData.sheet_width_mm ?? 1250)}
                             onChange={(e) => updateField("sheet_width_mm", parseFloat(e.target.value) || 0)}
                             className="input" disabled={!canEdit} data-testid="sheet-width" placeholder="0" />
                    </Field>
                    <Field label="Sheet Length (mm)">
                      <input type="number" step="1" value={(formData.sheet_length_mm ?? 2500) === 0 || (formData.sheet_length_mm ?? 2500) === "0" ? "" : (formData.sheet_length_mm ?? 2500)}
                             onChange={(e) => updateField("sheet_length_mm", parseFloat(e.target.value) || 0)}
                             className="input" disabled={!canEdit} data-testid="sheet-length" placeholder="0" />
                    </Field>
                  </>
                )}
              </>
            )}
            {formData.stock_form === "round_rod" && (
              <>
                <Field label="Rod Diameter (mm)"><DecimalInput value={formData.rod_diameter_mm || 0} onChange={(v) => updateField("rod_diameter_mm", v)} className="input" disabled={!canEdit} data-testid="rod-diameter" /></Field>
                <Field label="Rod Length / Pitch (mm)"><DecimalInput value={formData.rod_length_mm || 0} onChange={(v) => updateField("rod_length_mm", v)} className="input" disabled={!canEdit} data-testid="rod-length" /></Field>
                <Field label="Bar Stock Length (mm)"><DecimalInput value={formData.rod_stock_length_mm ?? 3048} onChange={(v) => updateField("rod_stock_length_mm", v)} className="input" disabled={!canEdit} data-testid="rod-stock-length" /></Field>
                <Field label="Bar End Loss (mm)"><DecimalInput value={formData.rod_end_loss_mm ?? 248} onChange={(v) => updateField("rod_end_loss_mm", v)} className="input" disabled={!canEdit} data-testid="rod-end-loss" /></Field>
                <div className="col-span-2 -mt-1 text-[11px] text-slate-400">Gross weight = full bar weight ÷ parts per bar, where parts per bar = (bar length − end loss) ÷ rod length/pitch. This spreads the wasted rod-end over each part (matches the costing sheet).</div>
              </>
            )}
            {formData.stock_form === "pipe" && (
              <>
                <Field label="Pipe OD (mm)"><DecimalInput value={formData.pipe_od_mm || 0} onChange={(v) => updateField("pipe_od_mm", v)} className="input" disabled={!canEdit} data-testid="pipe-od" /></Field>
                <Field label="Pipe ID (mm)"><DecimalInput value={formData.pipe_id_mm || 0} onChange={(v) => updateField("pipe_id_mm", v)} className="input" disabled={!canEdit} data-testid="pipe-id" /></Field>
                <Field label="Pipe Length (mm)"><DecimalInput value={formData.pipe_length_mm || 0} onChange={(v) => updateField("pipe_length_mm", v)} className="input" disabled={!canEdit} data-testid="pipe-length" /></Field>
              </>
            )}
            {formData.stock_form === "square_bar" && (
              <>
                <Field label="Bar Side (mm)"><DecimalInput value={formData.bar_side_mm || 0} onChange={(v) => updateField("bar_side_mm", v)} className="input" disabled={!canEdit} data-testid="bar-side" /></Field>
                <Field label="Bar Length (mm)"><DecimalInput value={formData.bar_length_mm || 0} onChange={(v) => updateField("bar_length_mm", v)} className="input" disabled={!canEdit} data-testid="bar-length" /></Field>
              </>
            )}
            {formData.stock_form === "hex_bar" && (
              <>
                <Field label="Across Flats (mm)"><DecimalInput value={formData.hex_across_flats_mm || 0} onChange={(v) => updateField("hex_across_flats_mm", v)} className="input" disabled={!canEdit} data-testid="hex-af" /></Field>
                <Field label="Bar Length (mm)"><DecimalInput value={formData.hex_length_mm || 0} onChange={(v) => updateField("hex_length_mm", v)} className="input" disabled={!canEdit} data-testid="hex-length" /></Field>
              </>
            )}

            {/* Multiple cavity — for double/multi-cavity parts the width & pitch describe the
                whole strip, so AUTO gross weight covers ALL cavities. Dividing by cavity_count
                gives the per-part gross weight that drives all costs. Net weight is NOT divided.
                Base gross_weight stays FULL; only the displayed/effective value is base ÷ cavities. */}
            <div className="col-span-2 mb-1">
              <div className="flex items-center gap-3 text-xs flex-wrap" data-testid="cavity-toggle">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    disabled={!canEdit}
                    checked={(Number(formData.cavity_count) || 1) > 1}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        cavity_count: e.target.checked ? Math.max(2, Number(p.cavity_count) || 2) : 1,
                        // Turning multi-cavity ON auto-ticks the blanking/forming press step(s).
                        // Turning multi-cavity ON ticks ONLY the blanking/forming press step(s)
                        // and clears the rest; turning OFF clears everything.
                        processes: (p.processes || []).map((pr) => ({
                          ...pr,
                          multi_cavity: e.target.checked && isBlankingOp(pr.name),
                        })),
                      }))
                    }
                    className="h-3.5 w-3.5 accent-orange-600"
                    data-testid="cavity-checkbox"
                  />
                  <span className="text-slate-600 font-medium">Is it multiple cavity?</span>
                </label>
                {(Number(formData.cavity_count) || 1) > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">Number of cavities:</span>
                    <input
                      type="number"
                      min={2}
                      step={1}
                      disabled={!canEdit}
                      value={Number(formData.cavity_count) || 2}
                      onChange={(e) => {
                        const v = Math.max(2, Math.floor(Number(e.target.value) || 2));
                        setFormData((p) => ({ ...p, cavity_count: v }));
                      }}
                      className="input w-24 py-1"
                      data-testid="cavity-count-input"
                      title="Type the number of cavities (e.g. 2, 4, 50…)"
                    />
                    <span className="text-[10px] text-slate-400">effective = entered ÷ {Number(formData.cavity_count) || 2} (feeds all costs)</span>
                  </div>
                )}
              </div>
            </div>

            <Field label="Gross Weight (kg)">
              <div className="flex items-center gap-2">
                <DecimalInput value={(Number(formData.gross_weight) || 0) / Math.max(1, Number(formData.cavity_count) || 1)} onChange={(v) => overrideGrossWeight((Number(v) || 0) * Math.max(1, Number(formData.cavity_count) || 1))} className="input flex-1" data-testid="gross-weight" disabled={!canEdit} />
                {!formData.gross_weight_overridden ? (
                  <span className="text-[10px] uppercase px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded font-semibold">auto</span>
                ) : canEdit && (
                  <button type="button" onClick={resetGrossWeight} className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1 whitespace-nowrap"><RotateCcw size={12} /> reset</button>
                )}
              </div>
              {!formData.gross_weight_overridden && (
                <div className="text-xs text-slate-400 mt-1">
                  Auto-calculated from {STOCK_FORMS.find(f => f.value === formData.stock_form)?.label.toLowerCase()} dimensions × ρ ({density} g/cc)
                  {formData.stock_form === "sheet_strip" && (
                    <span> · method: <strong>{formData.gross_weight_method === "coil" ? "coil / progressive" : "sheet layout"}</strong></span>
                  )}
                </div>
              )}
              {/* Round 18 — Sheet Utilization indicator (sheet_strip only) */}
              {formData.stock_form === "sheet_strip" && (() => {
                const sw = Number(formData.sheet_width_mm) || 0;
                const sl = Number(formData.sheet_length_mm) || 0;
                const strip_w = Number(formData.strip_width) || 0;
                const pitch_v = Number(formData.pitch) || 0;
                if (!sw || !sl || !strip_w || !pitch_v) return null;
                const cavities = Math.max(1, Number(formData.cavity_count) || 1);
                const strips = Math.floor(sl / strip_w);
                const pcs = Math.floor(sw / pitch_v);
                // Cut-positions per sheet (geometry). Each position yields `cavities` pieces.
                const positions = strips * pcs;
                if (positions <= 0) return null;
                const total = positions * cavities;
                // Utilization is purely geometric — sheet area consumed by the strip layout,
                // independent of how many cavities each cut-position holds.
                const usefulArea = positions * strip_w * pitch_v;
                const sheetArea = sw * sl;
                const util = (usefulArea / sheetArea) * 100;
                const color = util >= 85 ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : util >= 70 ? "bg-amber-50 text-amber-700 border-amber-200"
                                          : "bg-red-50 text-red-700 border-red-200";
                const hint = util >= 85 ? "Good layout"
                           : util >= 70 ? "Acceptable — could be tightened"
                                         : "Wasteful — try different pitch / strip width";
                const cavityStr = cavities > 1 ? ` (${positions} × ${cavities} cav)` : "";
                return (
                  <div
                    className={`mt-2 px-2.5 py-1.5 border rounded-md text-[11px] flex items-center justify-between ${color}`}
                    data-testid="sheet-utilization"
                  >
                    <span className="font-mono font-semibold">
                      {total} pcs/sheet{cavityStr} · {util.toFixed(1)}% utilization
                    </span>
                    <span className="italic">{hint}</span>
                  </div>
                );
              })()}
            </Field>
            <Field label="Net Weight (kg)"><DecimalInput value={formData.net_weight} onChange={(v) => updateField("net_weight", v)} className="input" data-testid="net-weight" disabled={!canEdit} /></Field>

            {/* Net / Gross ratio — shown for EVERY stock form (round rod, bar, sheet, …),
                not just sheet layout. Higher = less material wasted. */}
            <div className="col-span-2 -mt-1">
              {(() => {
                const cav = Math.max(1, Number(formData.cavity_count) || 1);
                const gwPc = (Number(formData.gross_weight) || 0) / cav;
                const nw = Number(formData.net_weight) || 0;
                if (!(gwPc > 0 && nw > 0)) return null;
                const ratio = (nw / gwPc) * 100;
                const color = ratio >= 60 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                            : ratio >= 40 ? "text-amber-700 bg-amber-50 border-amber-200"
                                          : "text-red-700 bg-red-50 border-red-200";
                return (
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-md text-[11px] ${color}`} data-testid="net-gross-ratio">
                    <span className="font-mono font-semibold">Net / Gross: {ratio.toFixed(1)}%</span>
                    <span className="text-slate-500">· material utilization ({nw.toFixed(4)} / {gwPc.toFixed(4)} kg)</span>
                  </div>
                );
              })()}
            </div>

            {/* Inline Raw Material Cost summary — derived from calc.gross_rmc / scrap_recovery / net_rmc */}
            <div className="col-span-2 mt-1 grid grid-cols-3 gap-3" data-testid="rmc-summary">
              <div className="border border-slate-200 rounded-md p-2.5 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Gross RMC (₹/pc)</div>
                <div className="font-mono text-base font-semibold text-slate-900">{formatINR(calc.gross_rmc)}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  = {((Number(formData.gross_weight) || 0) / Math.max(1, Number(formData.cavity_count) || 1)).toFixed(4)} kg × {formatINR(formData.rm_price_per_kg)}
                </div>
              </div>
              <div className="border border-slate-200 rounded-md p-2.5 bg-emerald-50">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 mb-0.5">Scrap Recovery (₹/pc)</div>
                <div className="font-mono text-base font-semibold text-emerald-800">− {formatINR(calc.scrap_recovery)}</div>
                <div className="text-[10px] text-emerald-700/70 mt-0.5">
                  = {Math.max(((Number(formData.gross_weight) || 0) / Math.max(1, Number(formData.cavity_count) || 1)) - (Number(formData.net_weight) || 0), 0).toFixed(4)} kg × {formatINR(formData.scrap_price_per_kg)} × {Number(formData.scrap_recovery_factor || 0.9).toFixed(2)}
                </div>
              </div>
              <div className="border border-orange-300 rounded-md p-2.5 bg-orange-50">
                <div className="text-[10px] uppercase tracking-wider text-orange-700 mb-0.5">Net RMC (A) (₹/pc)</div>
                <div className="font-mono text-base font-bold text-orange-700">{formatINR(calc.net_rmc)}</div>
                <div className="text-[10px] text-orange-700/70 mt-0.5">
                  = Gross RMC − Scrap Recovery
                </div>
              </div>
            </div>
            {/* R31 — Monthly RM requirement + low-volume flag + MOQ block.
                Hidden for Prototype quotes — a one-off run has no series volume / MOQ. */}
            {!isProto && (
              <SourcingBlock
                formData={formData}
                materials={materials}
                canEdit={canEdit}
                updateField={updateField}
                calc={calc}
              />
            )}
          </Section>

          <Section id="sec-b" wide title="B. Process Cost" subtitle="Per piece (₹/pc) or per kg (Shearing/Slitting × gross weight, other rows × net weight)">
            <ProcessesEditor
              items={formData.processes}
              onChange={(items) => updateField("processes", items)}
              disabled={!canEdit}
              grossWeight={(Number(formData.gross_weight) || 0) / Math.max(1, Number(formData.cavity_count) || 1)}
              netWeight={Number(formData.net_weight) || 0}
              cavityCount={Math.max(1, Number(formData.cavity_count) || 1)}
              total={calc.process_cost_total}
              monthlyVolume={(() => {
                const vols = formData.volumes || [];
                const peak = vols.reduce((b, v) => ((Number(v.annual_volume_pcs) || 0) > (Number(b?.annual_volume_pcs) || 0) ? v : b), null);
                return Number(peak?.monthly_volume_pcs) || (Number(peak?.annual_volume_pcs) || 0) / 12 || 0;
              })()}
            />
          </Section>

          <Section id="sec-c" wide title="C. Bought Out Parts (BOP)" subtitle="External items purchased for the part">
            <ItemListEditor
              items={formData.bop_items}
              onChange={(items) => updateField("bop_items", items)}
              disabled={!canEdit}
              columns={[
                { key: "name", label: "Item", type: "text", width: "" },
                { key: "qty", label: "Qty", type: "number", width: "w-20" },
                { key: "unit_price", label: "₹/unit", type: "number", width: "w-28 text-right" },
              ]}
              totalLabel="Total BOP Cost (C)"
              total={calc.bop_cost_total}
              addLabel="Add BOP item"
              testId="bop-editor"
            />
          </Section>

          <Section id="sec-d" wide title="D. Treatment & Maintenance" subtitle="Surface & Heat treatments support ₹/pc, ₹/kg, or ₹/sq.in modes">
            <TreatmentField
              label="Surface Treatment"
              value={formData.surface_treatment}
              onChange={(v) => updateField("surface_treatment", v)}
              netWeightKg={formData.net_weight}
              placeholderNotes="e.g. MFZn2-C, Black Cation ED Coating"
              disabled={!canEdit}
              testId="surface-treatment-field"
            />
            <TreatmentField
              label="Heat Treatment"
              value={formData.heat_treatment}
              onChange={(v) => updateField("heat_treatment", v)}
              netWeightKg={formData.net_weight}
              placeholderNotes="e.g. Induction hardening, Carburising"
              disabled={!canEdit}
              testId="heat-treatment-field"
            />

            {/* Tool Maintenance — % of Process Cost OR fixed ₹/pc lumpsum. Backend reads
                tool_maintenance_overridden + tool_maintenance_fixed when overridden=true. */}
            {!isProto && (
            <div className="col-span-2 border border-slate-200 rounded-md p-3 bg-white" data-testid="tool-maintenance-field">
              <div className="flex justify-between items-start mb-2 gap-3">
                <label className="text-sm font-semibold text-slate-800">Tool Maintenance</label>
                <div className="text-right flex-shrink-0">
                  <div className="text-2xl font-bold text-orange-600 leading-tight font-mono">{formatINR(calc.tool_maintenance)}</div>
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider">/pc</div>
                </div>
              </div>

              {/* Mode toggle */}
              <div className="flex items-center gap-2 mb-2 text-xs">
                <span className="text-slate-500">Mode:</span>
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => updateField("tool_maintenance_overridden", false)}
                  className={`px-2.5 py-1 rounded border ${
                    !formData.tool_maintenance_overridden
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid="tool-maint-mode-pct"
                >
                  % of Process Cost
                </button>
                <button
                  type="button"
                  disabled={!canEdit}
                  onClick={() => updateField("tool_maintenance_overridden", true)}
                  className={`px-2.5 py-1 rounded border ${
                    formData.tool_maintenance_overridden
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid="tool-maint-mode-lumpsum"
                >
                  Fixed ₹/pc (lumpsum)
                </button>
              </div>

              {!formData.tool_maintenance_overridden ? (
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-0.5">% of Process Cost (B)</label>
                    <div className="relative">
                      <DecimalInput
                        value={formData.tool_maintenance_pct ?? 10}
                        onChange={(v) => updateField("tool_maintenance_pct", v)}
                        className="w-full px-2 py-1 pr-7 border border-slate-200 rounded text-sm font-mono text-right"
                        data-testid="tool-maint-pct"
                        disabled={!canEdit}
                      />
                      <span className="absolute right-2 top-1.5 text-xs text-slate-400 pointer-events-none">%</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 pb-1.5 font-mono">
                    = {Number(formData.tool_maintenance_pct || 0).toFixed(1)}% × {formatINR(calc.process_cost_total)}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-0.5">Fixed amount (₹ per piece)</label>
                    <div className="relative">
                      <DecimalInput
                        value={formData.tool_maintenance_fixed ?? 0}
                        onChange={(v) => updateField("tool_maintenance_fixed", v)}
                        className="w-full px-2 py-1 pl-6 border border-slate-200 rounded text-sm font-mono text-right"
                        data-testid="tool-maint-fixed"
                        disabled={!canEdit}
                      />
                      <span className="absolute left-2 top-1.5 text-xs text-slate-400 pointer-events-none">₹</span>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 pb-1.5 font-mono">
                    Fixed lumpsum — independent of Process Cost (B)
                  </div>
                </div>
              )}
            </div>
            )}

            <Field label="Tooling Cost (₹, one-time)">
              <GroupedNumberInput value={formData.tooling_cost}
                onChange={(v) => updateField("tooling_cost", v || 0)}
                className="input" disabled={!canEdit} data-testid="tooling-cost" placeholder="0" />
            </Field>
            <Field label="Tooling Life (cycles)">
              <GroupedNumberInput value={formData.tooling_life}
                onChange={(v) => updateField("tooling_life", v || 0)}
                className="input" disabled={!canEdit} data-testid="tooling-life" placeholder="0" />
            </Field>

            {/* R32 — Amortize-over-cycles override */}
            <Field
              label="Amortize over (cycles)"
              hint={`Defaults to tooling life. Override for short customer commitments. Max: ${Number(formData.tooling_life || 0).toLocaleString("en-IN")}`}
            >
              <GroupedNumberInput
                allowNull
                min="0"
                max={formData.tooling_life || undefined}
                placeholder={formData.tooling_life ? Number(formData.tooling_life).toLocaleString("en-IN") : ""}
                value={formData.amortize_over_cycles ?? ""}
                onChange={(v) => {
                  if (v != null && formData.tooling_life > 0 && v > formData.tooling_life) {
                    toast.error(`Cannot exceed tooling life (${Number(formData.tooling_life).toLocaleString("en-IN")})`);
                    return;
                  }
                  updateField("amortize_over_cycles", v);
                }}
                disabled={!canEdit}
                className="input"
                data-testid="amortize-over-cycles"
              />
            </Field>
            {/* R32 — Side-by-side: Full-life vs Applied per-piece.
                Always renders the wrapper (with testid) so automated checks
                can locate it; cards inside render conditionally on tooling values. */}
            {(() => {
              const t = deriveTooling(formData);
              const hasValues = t.applied_per_piece || t.full_life_per_piece;
              return (
                <div className="col-span-2 grid grid-cols-2 gap-3 text-xs" data-testid="tooling-amort-preview">
                  {!hasValues && (
                    <div className="col-span-2 text-[11px] italic text-slate-400">
                      Enable “Amortize tooling cost” + set Tooling Cost &amp; Life to preview per-piece.
                    </div>
                  )}
                  {hasValues && t.has_override && (
                    <div className="border border-slate-200 rounded p-2 bg-slate-50" data-testid="tooling-amort-fulllife">
                      <div className="text-slate-500">Full-life amortization</div>
                      <div className="font-mono text-slate-700">{formatINR(t.full_life_per_piece)}/pc</div>
                      <div className="text-[10px] text-slate-400">
                        over {Number(formData.tooling_life || 0).toLocaleString("en-IN")} cycles
                      </div>
                    </div>
                  )}
                  {hasValues && (
                    <div
                      className={`border rounded p-2 ${t.has_override ? "border-orange-300 bg-orange-50" : "border-slate-200 bg-slate-50"}`}
                      data-testid="tooling-amort-applied"
                    >
                      <div className="text-slate-500 font-semibold">Applied amortization</div>
                      <div className="font-mono font-semibold text-slate-900">{formatINR(t.applied_per_piece)}/pc</div>
                      <div className="text-[10px] text-slate-400">
                        over {Number(t.basis_cycles || 0).toLocaleString("en-IN")} cycles
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Other One-time Development Cost — repeatable rows (one-time only, not in ₹/pc) */}
            <div className="col-span-2 border border-slate-200 rounded-md p-3 bg-white" data-testid="other-dev-cost-section">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="text-sm font-semibold text-slate-800">Other One-time Development Cost</div>
                  <p className="text-[11px] text-slate-500">One-time charges (testing, machinery, etc.) — not part of the per-piece Grand Total.</p>
                </div>
                <button
                  type="button"
                  onClick={addOtherDevRow}
                  disabled={!canEdit}
                  className="text-xs font-medium text-orange-600 hover:text-orange-700 disabled:opacity-40"
                  data-testid="other-dev-cost-add"
                >
                  + Add development cost
                </button>
              </div>
              {(formData.other_dev_costs || []).length === 0 ? (
                <div className="text-[11px] italic text-slate-400">No development cost lines added.</div>
              ) : (
                <div className="space-y-2">
                  {(formData.other_dev_costs || []).map((row, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`other-dev-cost-row-${idx}`}>
                      <select
                        value={row.category || "Other"}
                        onChange={(e) => updateOtherDevRow(idx, "category", e.target.value)}
                        disabled={!canEdit}
                        className="input col-span-3 text-sm"
                        data-testid={`other-dev-cost-category-${idx}`}
                      >
                        {devCategories.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                      <div className="relative col-span-3">
                        <input
                          type="number"
                          step="100"
                          value={(Number(row.amount) || 0) === 0 ? "" : row.amount}
                          onChange={(e) => updateOtherDevRow(idx, "amount", parseFloat(e.target.value) || 0)}
                          disabled={!canEdit}
                          placeholder="0"
                          className="input pl-6 text-sm font-mono text-right"
                          data-testid={`other-dev-cost-amount-${idx}`}
                        />
                        <span className="absolute left-2 top-2 text-xs text-slate-400 pointer-events-none">₹</span>
                      </div>
                      <input
                        type="text"
                        value={row.remarks || ""}
                        onChange={(e) => updateOtherDevRow(idx, "remarks", e.target.value)}
                        disabled={!canEdit}
                        placeholder="Remarks (optional)"
                        className="input col-span-5 text-sm"
                        data-testid={`other-dev-cost-remarks-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeOtherDevRow(idx)}
                        disabled={!canEdit}
                        className="col-span-1 text-slate-400 hover:text-red-600 disabled:opacity-40 text-center"
                        data-testid={`other-dev-cost-remove-${idx}`}
                        aria-label="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t border-dashed border-slate-200 text-sm">
                    <span className="text-slate-600 font-medium">Sum of development costs</span>
                    <span className="font-mono font-semibold text-slate-900" data-testid="other-dev-cost-total">{formatINR(otherDevTotal)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Tool Amortization Toggle — default OFF */}
            <div className="col-span-2 border border-slate-200 rounded-md p-3 bg-slate-50" data-testid="amortize-tooling-block">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      id="amortize-tooling"
                      type="checkbox"
                      checked={!!formData.amortize_tooling}
                      onChange={(e) => updateField("amortize_tooling", e.target.checked)}
                      disabled={!canEdit}
                      className="h-4 w-4 rounded border-slate-300 text-orange-600 focus:ring-orange-500"
                      data-testid="amortize-tooling-toggle"
                    />
                    <label htmlFor="amortize-tooling" className="text-sm font-semibold text-slate-800 cursor-pointer">
                      Amortize tooling cost into ₹/pc (Section F)
                    </label>
                  </div>
                  <p className="text-[11px] text-slate-500 ml-6">
                    Default: <strong>OFF</strong>. Turn ON only when the customer specifically asks for amortized pricing. When OFF, the tooling cost is charged separately (one-time) and shown as a separate line in the Excel quote.
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  {formData.amortize_tooling && formData.tooling_life > 0 ? (
                    <>
                      <div className="text-xl font-bold text-orange-600 leading-tight font-mono">
                        {formatINR(formData.tooling_cost / formData.tooling_life)}
                      </div>
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider">F · ₹/pc</div>
                    </>
                  ) : (
                    <div className="text-[11px] text-slate-400 italic max-w-[180px]">
                      {formData.tooling_cost > 0
                        ? `₹${formData.tooling_cost.toLocaleString("en-IN")} charged separately`
                        : "—"}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Section>

          <Section id="sec-e" wide title="E. Overheads & Margin" subtitle="Combined overheads + margin loadings. Toggle % basis or ₹ lump-sum per row.">
            <div className="col-span-2">
              <OverheadsTable
                overheads={(() => {
                  // R43 — surface the R32 auto-injected "Inventory Carrying Cost" row
                  // in the editable table so the engineer sees what Excel will print.
                  // The row is read-only by virtue of is_auto and is filtered out on save.
                  const base = formData.overheads || [];
                  const extras = (calc.overheads_calculated || []).filter(
                    (r) => r.is_auto && !base.some((b) => b.is_auto && (b.label || "") === (r.label || ""))
                  );
                  return [...base, ...extras];
                })()}
                onChange={(o) => updateField("overheads", (o || []).filter((r) => !r.is_auto))}
                calculations={calc.overheads_calculated}
                readOnly={!canEdit}
              />
            </div>
          </Section>

          <Section
            id="sec-f"
            wide
            title="F. Custom Adjustments"
            subtitle="Customer markups, discounts, or fixed surcharges applied to Grand Total. All rows use the same base (Grand Total before adjustments) and don't compound."
          >
            <div className="col-span-2" data-testid="custom-adjustments-editor">
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-10">#</th>
                    <th className="text-left p-2">Label</th>
                    <th className="text-left p-2 w-40">Mode</th>
                    <th className="text-right p-2 w-28">Value</th>
                    <th className="text-right p-2 w-32">₹/pc</th>
                    <th className="w-12"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(formData.custom_adjustments || []).length === 0 && (
                    <tr>
                      <td colSpan={6} className="p-4 text-center text-sm text-slate-400">
                        No custom adjustments. {canEdit && "Click \"Add adjustment\" below."}
                      </td>
                    </tr>
                  )}
                  {(formData.custom_adjustments || []).map((row, idx) => {
                    const mode = row.mode || "pct";
                    const value = Number(row.value) || 0;
                    const gtBeforeAdj = Number(calc.grand_total_before_adj) || 0;
                    const amount = mode === "pct" ? gtBeforeAdj * (value / 100) : value;
                    return (
                      <tr key={idx}>
                        <td className="p-1.5 text-slate-400 font-mono text-xs text-center">{idx + 1}</td>
                        <td className="p-1.5">
                          <input
                            type="text"
                            value={row.label || ""}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const next = [...formData.custom_adjustments];
                              next[idx] = { ...next[idx], label: e.target.value };
                              updateField("custom_adjustments", next);
                            }}
                            placeholder="e.g. TACO 5% markup"
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                            data-testid={`adj-label-${idx}`}
                          />
                        </td>
                        <td className="p-1.5">
                          <select
                            value={mode}
                            disabled={!canEdit}
                            onChange={(e) => {
                              const next = [...formData.custom_adjustments];
                              next[idx] = { ...next[idx], mode: e.target.value };
                              updateField("custom_adjustments", next);
                            }}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                            data-testid={`adj-mode-${idx}`}
                          >
                            <option value="pct">% of Grand Total</option>
                            <option value="lumpsum">₹ per piece</option>
                          </select>
                        </td>
                        <td className="p-1.5">
                          <DecimalInput
                            disabled={!canEdit}
                            allowNegative
                            value={value}
                            onChange={(v) => {
                              const next = [...formData.custom_adjustments];
                              next[idx] = { ...next[idx], value: v };
                              updateField("custom_adjustments", next);
                            }}
                            className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                            data-testid={`adj-value-${idx}`}
                            title={mode === "pct" ? "Percent — negative allowed (discount)" : "Lumpsum ₹/pc — negative allowed (discount)"}
                          />
                        </td>
                        <td className="p-1.5 font-mono text-right text-slate-700">
                          {amount >= 0 ? "+ " : "− "}{formatINR(Math.abs(amount))}
                        </td>
                        <td className="p-1.5 text-right">
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => {
                                const next = formData.custom_adjustments.filter((_, i) => i !== idx);
                                updateField("custom_adjustments", next);
                              }}
                              className="text-slate-300 hover:text-red-500"
                              title="Remove"
                              data-testid={`adj-remove-${idx}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="border-t border-slate-200 p-2 flex justify-between items-center bg-slate-50">
                {canEdit ? (
                  <button
                    type="button"
                    onClick={() => updateField("custom_adjustments", [
                      ...(formData.custom_adjustments || []),
                      { label: "", mode: "pct", value: 0 },
                    ])}
                    className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
                    data-testid="adj-add-btn"
                  >
                    <Plus size={14} /> Add adjustment
                  </button>
                ) : <div />}
                <div className="text-sm">
                  <span className="text-slate-500">Total Section F:</span>{" "}
                  <strong className="font-mono text-slate-900">
                    {(Number(calc.custom_adjustments_total) || 0) >= 0 ? "+ " : "− "}
                    {formatINR(Math.abs(Number(calc.custom_adjustments_total) || 0))} / pc
                  </strong>
                </div>
              </div>

              {/* Customer-visible toggle */}
              <div className="mt-3 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md" data-testid="adj-visibility-toggle-row">
                <input
                  type="checkbox"
                  id="adj-visible"
                  checked={!!formData.custom_adjustments_visible_in_quote}
                  disabled={!canEdit}
                  onChange={(e) => updateField("custom_adjustments_visible_in_quote", e.target.checked)}
                  className="mt-0.5"
                  data-testid="adj-visible-toggle"
                />
                <label htmlFor="adj-visible" className="text-sm text-amber-900 leading-snug cursor-pointer">
                  <span className="font-semibold">Show adjustments in customer quote</span>
                  <div className="text-xs text-amber-800/80 mt-0.5">
                    When OFF (default), customer sees only the final Grand Total — the adjustment lines are hidden from the PDF and Excel export.
                    When ON, the customer sees each adjustment row and total in their quote.
                  </div>
                </label>
              </div>
            </div>
          </Section>

          <Section wide title="Commercial">
            <Field label="Selling Price (= Grand Total, auto)">
              <div className="flex items-center px-3 py-2 border border-slate-200 rounded-md bg-slate-50">
                <span className="font-mono text-sm font-semibold text-slate-900">{formatINR(calc.grand_total)}</span>
                <span className="ml-2 text-[10px] uppercase tracking-wider text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded">auto = GT</span>
              </div>
            </Field>
            <Field label="Target Price from Customer (₹/pc, optional)">
              <DecimalInput value={formData.target_price || 0} onChange={(v) => updateField("target_price", v)} className="input" disabled={!canEdit} />
            </Field>
          </Section>

          {!isProto && (<>
          <div className="mb-3 flex items-center gap-2">
            <input
              type="checkbox"
              id="npd-show-yoy"
              checked={showYoy}
              onChange={(e) => {
                const next = e.target.checked;
                setShowYoy(next);
                if (!next) {
                  // Unticking clears the discount so the schedule above flattens to GT.
                  setFormData((p) => ({ ...p, yoy_reduction_pct: 0, yoy_apply_years: null }));
                }
              }}
              className="w-4 h-4 cursor-pointer"
              data-testid="npd-show-yoy-toggle"
            />
            <label htmlFor="npd-show-yoy" className="text-sm font-medium text-slate-700 cursor-pointer select-none">
              Apply year-over-year price reduction
            </label>
          </div>
          {showYoy && (
          <Section wide title="YoY Price Reduction" subtitle="Customer-agreed annual price discount. The Year-by-Year Schedule above will reflect the discounted prices.">
            <Field label="YoY Reduction (% per year, compound)">
              <DecimalInput
                value={formData.yoy_reduction_pct ?? 0}
                onChange={(v) => updateField("yoy_reduction_pct", v)}
                className="input"
                data-testid="yoy-pct"
                disabled={!canEdit}
              />
            </Field>
            <Field label="YoY Apply Years (cap, optional)">
              <input
                type="number" step="1" min="1" max="20"
                value={formData.yoy_apply_years ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  updateField("yoy_apply_years", v === "" ? null : (parseInt(v) || null));
                }}
                placeholder="No cap"
                className="input"
                data-testid="yoy-apply-years"
                disabled={!canEdit}
              />
              <div className="text-[11px] text-slate-500 mt-1">Number of times the discount is applied. E.g. 3 = discount applied in Y2, Y3, Y4, then Y5+ stays the same. Leave blank for no cap.</div>
            </Field>

            <div className="col-span-2 mt-2">
              <div className="flex items-center justify-between mb-2 gap-3 flex-wrap">
                <div className="text-xs font-medium text-slate-600">YoY Price Schedule</div>
                <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-full px-3 py-1 font-medium" data-testid="yoy-total-revenue-pill">
                  Total Project Revenue: <span className="font-mono font-bold">{formatINR(calc.total_project_value || 0)}</span>
                </div>
              </div>
              <table className="w-full text-sm border border-slate-200 rounded overflow-hidden" data-testid="yoy-price-schedule">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left p-2 w-16">Year</th>
                    <th className="text-left p-2 w-24">Calendar</th>
                    <th className="text-right p-2 w-32">Reduction %</th>
                    <th className="text-right p-2">Price/pc</th>
                    <th className="text-right p-2">Δ vs Y1</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(calc.yoy_schedule || []).map((row) => {
                    const n = row.year_number;
                    const base = calc.grand_total || 1;
                    const deltaPct = n === 1 ? 0 : ((row.price_per_pc / base) - 1) * 100;
                    return (
                      <tr key={n}>
                        <td className="p-2 font-mono text-slate-500">Y{n}</td>
                        <td className="p-2">{row.year_label || "—"}</td>
                        <td className="p-2 text-right">
                          {n === 1 ? <span className="text-slate-400 text-xs">base</span> : (
                            <div className="inline-flex items-center gap-1 justify-end">
                              <input
                                type="number" step="0.01"
                                value={yoyPerYearVal(n)}
                                onChange={(e) => setYoyPerYear(n, e.target.value)}
                                disabled={!canEdit}
                                className="w-16 px-1.5 py-0.5 border border-slate-300 rounded text-right text-xs font-mono disabled:bg-slate-100"
                                data-testid={`yoy-pct-y${n}`}
                                title="Reduction for this year vs the previous year. Blank = use the global rate above."
                              />
                              <span className="text-slate-400 text-xs">%</span>
                            </div>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono font-semibold">{formatINR(row.price_per_pc)}</td>
                        <td className="p-2 text-right font-mono text-xs">
                          {n === 1 ? <span className="text-slate-400">base</span> : (
                            <span className={row.yoy_capped ? "text-slate-400" : "text-emerald-600"}>
                              {deltaPct.toFixed(2)}%{row.yoy_capped ? " (capped)" : ""}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-500 mt-2">
                Each year's price = previous year × (1 − that year's reduction %). Edit any year's box to set it manually; a blank box uses the global rate above. Included in the customer-facing preview and Excel export.
              </p>
            </div>
          </Section>
          )}
          </>)}

          <Section wide title="Remarks">
            <div className="col-span-2">
              <textarea rows={3} value={formData.remarks || ""} onChange={(e) => updateField("remarks", e.target.value)} className="input" placeholder="Internal notes, negotiation history, RFQ details…" disabled={!canEdit} />
            </div>
          </Section>

          {isEdit && (
            <div className="relative z-10 lg:w-[calc(100%+356px)] grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
            <Section title="Drawing" subtitle="Customer drawing (PDF, JPG, PNG, TIF · max 10 MB). Thumbnail appears in the Excel export.">
              <DrawingUpload
                npdId={id}
                drawing={{
                  drawing_file_path: formData.drawing_file_path,
                  drawing_thumbnail_path: formData.drawing_thumbnail_path,
                  drawing_original_filename: formData.drawing_original_filename,
                  drawing_uploaded_at: formData.drawing_uploaded_at,
                  drawing_size_bytes: formData.drawing_size_bytes,
                }}
                canEdit={canEdit}
                onChanged={(d) => {
                  setFormData((p) => ({
                    ...p,
                    drawing_file_path: d?.drawing_file_path || null,
                    drawing_thumbnail_path: d?.drawing_thumbnail_path || null,
                    drawing_original_filename: d?.drawing_original_filename || null,
                    drawing_uploaded_at: d ? new Date().toISOString() : null,
                    drawing_size_bytes: d?.drawing_size_bytes || null,
                  }));
                }}
              />
            </Section>
            <Section
              title="Drawing Screenshot"
              subtitle="Mandatory JPG/PNG (max 2 MB) embedded in the Section A 'RAW MATERIAL' panel of the quote."
            >
              <ScreenshotUpload
                scope="npd"
                entityId={id}
                path={formData.drawing_screenshot_path}
                uploadedAt={formData.drawing_screenshot_uploaded_at}
                sizeBytes={formData.drawing_screenshot_size_bytes}
                canEdit={canEdit}
                onChanged={(d) => {
                  setFormData((p) => ({
                    ...p,
                    drawing_screenshot_path: d?.drawing_screenshot_path || null,
                    drawing_screenshot_uploaded_at: d?.drawing_screenshot_uploaded_at || null,
                    drawing_screenshot_size_bytes: d?.drawing_screenshot_size_bytes || null,
                  }));
                }}
              />
            </Section>
            <Section
              title="RFQ Mail"
              subtitle={formData.project_id
                ? "Optional here — attached once for the whole project; parts share it. Auto-fills the APQP 'RFQ MAIL' deliverable on Won."
                : "Mandatory — customer RFQ email/attachment (PDF, MSG, EML, JPG, PNG, DOC, XLS · max 10 MB). Auto-fills the APQP 'RFQ MAIL' deliverable on Won."}
            >
              <RfqMailUpload
                npdId={id}
                rfq={{
                  rfq_mail_path: formData.rfq_mail_path,
                  rfq_mail_original_filename: formData.rfq_mail_original_filename,
                  rfq_mail_uploaded_at: formData.rfq_mail_uploaded_at,
                  rfq_mail_size_bytes: formData.rfq_mail_size_bytes,
                }}
                canEdit={canEdit}
                onChanged={(d) => {
                  setFormData((p) => ({
                    ...p,
                    rfq_mail_path: d?.rfq_mail_path || null,
                    rfq_mail_original_filename: d?.rfq_mail_original_filename || null,
                    rfq_mail_uploaded_at: d?.rfq_mail_uploaded_at || null,
                    rfq_mail_size_bytes: d?.rfq_mail_size_bytes || null,
                  }));
                }}
              />
            </Section>
            </div>
          )}

          {isEdit && (
            <div className="relative z-10 lg:w-[calc(100%+356px)]">
              <MiscFilesPanel
                entityType="npd"
                entityId={id}
                files={formData.misc_files || []}
                canEdit={canEdit}
                onChange={async () => {
                  const r = await api.get(`/npd/${id}`);
                  setFormData((p) => ({ ...p, misc_files: r.data.misc_files || [] }));
                }}
              />
            </div>
          )}

          {isEdit && rounds.length > 0 && (() => {
            const CURRENT_KEY = "_current_";
            const leftId = comparingRound?.left || (rounds.length > 0 ? rounds[rounds.length - 1].round_id : CURRENT_KEY);
            const rightId = comparingRound?.right || CURRENT_KEY;
            const resolveSide = (id) => {
              if (id === CURRENT_KEY) return { label: "Current", npd: { ...formData, selling_price: calc.grand_total } };
              const r = rounds.find((x) => x.round_id === id);
              return r ? { label: r.round_label, npd: r.snapshot || {} } : { label: "?", npd: {} };
            };
            const leftSide = resolveSide(leftId);
            const rightSide = resolveSide(rightId);
            const sameSelected = leftId === rightId;
            const setSide = (which, id) => setComparingRound({
              left: which === "left" ? id : leftId,
              right: which === "right" ? id : rightId,
            });
            return (
              <div id="rounds-section" className="bg-white border border-slate-200 rounded-lg p-5">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><History size={16} /> Negotiation Rounds ({rounds.length})</h3>

                <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-md">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Compare any two</div>
                  <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="npd-compare-picker">
                    <select value={leftId} onChange={(e) => setSide("left", e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="npd-compare-left">
                      <option value={CURRENT_KEY}>Current (live)</option>
                      {rounds.map((r) => (<option key={r.round_id} value={r.round_id}>{r.round_label} · {new Date(r.round_date).toLocaleDateString("en-IN")}</option>))}
                    </select>
                    <span className="text-slate-500 text-xs">vs</span>
                    <select value={rightId} onChange={(e) => setSide("right", e.target.value)} className="px-2 py-1 border border-slate-300 rounded text-xs bg-white" data-testid="npd-compare-right">
                      <option value={CURRENT_KEY}>Current (live)</option>
                      {rounds.map((r) => (<option key={r.round_id} value={r.round_id}>{r.round_label} · {new Date(r.round_date).toLocaleDateString("en-IN")}</option>))}
                    </select>
                    {sameSelected && <span className="text-[11px] text-amber-600">Pick two different sides</span>}
                  </div>
                  {!sameSelected && (
                    <RoundCompareView
                      round={{ round_id: leftId, round_label: leftSide.label, round_date: leftId === CURRENT_KEY ? new Date().toISOString() : (rounds.find((x) => x.round_id === leftId)?.round_date || ""), notes: leftId === CURRENT_KEY ? "" : (rounds.find((x) => x.round_id === leftId)?.notes || ""), snapshot: leftSide.npd }}
                      currentNpd={rightSide.npd}
                      rightLabel={rightSide.label}
                      npdId={id}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  {rounds.map((r) => {
                    const snapCalc = computeNpd(r.snapshot || {});
                    const isShortcut = leftId === r.round_id && rightId === CURRENT_KEY;
                    return (
                      <div key={r.round_id} className={`border rounded-md p-3 ${isShortcut ? "border-sky-400 bg-sky-50" : "border-slate-200 bg-slate-50"}`}>
                        <div className="flex items-center justify-between mb-1 gap-2">
                          <div className="min-w-0">
                            <span className="inline-block text-xs font-bold bg-sky-100 text-sky-700 px-2 py-0.5 rounded">{r.round_label}</span>
                            <span className="ml-2 text-xs text-slate-500">{new Date(r.round_date).toLocaleString("en-IN")}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="text-sm font-mono font-semibold text-slate-900">{formatINR(snapCalc.grand_total)}</div>
                            <button
                              onClick={() => window.open(`/npd/${id}/rounds/${r.round_id}/preview`, "_blank", "noopener")}
                              className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50 whitespace-nowrap inline-flex items-center gap-1"
                              data-testid={`view-round-${r.round_label}`}
                              title={`View the ${r.round_label} quote as saved`}
                            >
                              <Eye size={13} /> View
                            </button>
                            <button
                              onClick={() => setComparingRound({ left: r.round_id, right: CURRENT_KEY })}
                              className={`text-xs px-2 py-1 rounded border whitespace-nowrap ${isShortcut ? "bg-sky-600 text-white border-sky-600" : "border-sky-500 text-sky-700 hover:bg-sky-50"}`}
                              data-testid={`compare-round-${r.round_label}`}
                              title="Quick compare this round vs Current"
                            >
                              vs Current
                            </button>
                          </div>
                        </div>
                        {r.notes && <div className="text-xs text-slate-600 mt-1">{r.notes}</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        <div className="lg:order-last">
          <LiveCalcPanel calc={calc} threshold={threshold} hideMargin={hideMargin} targetPrice={formData.target_price} rmcReasons={rmcReasons} screenshotScope="npd" entityId={id} screenshotPath={formData.drawing_screenshot_path}
            settledPrice={formData.customer_settled_price} settledDev={formData.customer_settled_dev}
            processes={formData.processes}
            monthlyVolume={(() => {
              const vols = formData.volumes || [];
              const peak = vols.reduce((b, v) => ((Number(v.annual_volume_pcs) || 0) > (Number(b?.annual_volume_pcs) || 0) ? v : b), null);
              return Number(peak?.monthly_volume_pcs) || (Number(peak?.annual_volume_pcs) || 0) / 12 || 0;
            })()} />
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children, id, wide }) {
  return (
    <div id={id} className={`bg-white border border-slate-200 rounded-lg p-5 scroll-mt-4 transition-all ${wide ? "relative z-10 lg:w-[calc(100%+356px)]" : ""}`}>
      <div className="mb-4">
        <h3 className="font-semibold text-base text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

// Searchable dropdown for the per-row Process name — filters the Operations Bank list as you type.
function ProcessNamePicker({ value, disabled, options, testId, onChange, onCreate }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const inMaster = !!value && (options || []).some((o) => (o.name || "") === value);
  const q = query.trim().toLowerCase();
  const filtered = (options || []).filter((o) => (o.name || "").toLowerCase().includes(q));
  const qExact = (options || []).some((o) => (o.name || "").trim().toLowerCase() === q);
  const choose = (name) => { onChange(name); setOpen(false); setQuery(""); };
  const addNew = async () => {
    const nm = query.trim();
    if (!nm || creating) return;
    setCreating(true);
    try {
      const op = onCreate ? await onCreate(nm) : null;
      if (op && op.name) choose(op.name);
      else choose(nm);   // fall back to just using the typed name
    } finally {
      setCreating(false);
    }
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => { if (!disabled) { setOpen((o) => !o); setQuery(""); } }}
        className="w-full px-2 py-1 border border-slate-200 rounded text-sm bg-white text-left flex items-start justify-between gap-1 disabled:opacity-60"
        data-testid={testId}
      >
        <span className={`whitespace-normal break-words leading-tight ${value ? "" : "text-slate-400"}`}>
          {value || "Select process…"}
        </span>
        <ChevronDown size={14} className={`shrink-0 mt-0.5 text-slate-400 transition ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 w-full min-w-[200px] bg-white border border-slate-200 rounded-md shadow-lg">
          <div className="p-1.5 border-b border-slate-100">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search process…"
              className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1 text-sm">
            {value && !inMaster && (
              <li onClick={() => choose(value)} className="px-3 py-1.5 hover:bg-orange-50 cursor-pointer">
                {value}
              </li>
            )}
            {filtered.length === 0 && !query.trim() && (
              <li className="px-3 py-1.5 text-slate-400 italic">No match</li>
            )}
            {filtered.map((o, i) => (
              <li
                key={o.operation_id || o.name || i}
                onClick={() => choose(o.name)}
                className="px-3 py-1.5 hover:bg-sky-50 cursor-pointer"
              >
                {o.name}
              </li>
            ))}
            {query.trim() && !qExact && onCreate && (
              <li className="border-t border-slate-100">
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={addNew}
                  disabled={creating}
                  className="w-full text-left px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 flex items-center gap-1.5 disabled:opacity-60"
                  data-testid="process-add-new"
                >
                  <Plus size={13} /> {creating ? "Adding…" : <>Add <strong>{query.trim()}</strong> as a new process</>}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// Round 18 — Process Cost editor with per_piece / per_kg modes
// Machine types that bill by Machine-Hour Rate (time-based) — show the MHR tick + auto-calc.
const MHR_TYPES = ["cnc", "vmc", "sliding_head"];
// Resolve the MHR rate key for a step from either its machine_type OR the machine
// NAME chosen in the Tonnage column (e.g. a machine literally named "CNC" / "VMC" /
// "Sliding Head" whose machine_type badge is still PRESS/OTHER).
const mhrKeyFor = (it) => {
  const mt = String(it?.machine_type || "").toLowerCase();
  if (MHR_TYPES.includes(mt)) return mt;
  const ton = String(it?.tonnage || "").toLowerCase();
  if (/sliding\s*head|sliding_head/.test(ton)) return "sliding_head";
  if (/\bvmc\b/.test(ton)) return "vmc";
  if (/\bcnc\b/.test(ton)) return "cnc";
  return "";
};
// A step is MHR-eligible if we can resolve it to CNC / VMC / Sliding Head.
const isMhrType = (it) => mhrKeyFor(it) !== "";

function ProcessesEditor({ items, onChange, disabled, grossWeight, netWeight, total, monthlyVolume = 0, cavityCount = 1 }) {
  const cav = Math.max(1, Number(cavityCount) || 1);
  const isMulti = cav > 1;
  // Are there any CNC/VMC/sliding-head steps that CAN bill by MHR?
  const anyMhrEligible = (items || []).some(isMhrType);
  // MHR mode — the header toggle. When ON, the MHR tick-box column appears in
  // front of CNC/VMC/Sliding-Head rows. Default ON if any step is already MHR.
  const [mhrMode, setMhrMode] = useState(() => (items || []).some((it) => it.use_mhr));
  // Show the MHR column only while MHR mode is on (and some row is eligible).
  const hasMhr = mhrMode && anyMhrEligible;
  // Existing NPDs load their rows after mount — if any arrives already flagged MHR,
  // switch the header toggle on so its tick boxes are visible.
  useEffect(() => {
    if (!mhrMode && (items || []).some((it) => it.use_mhr)) setMhrMode(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  const [masterProcs, setMasterProcs] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  // Internal machine tonnage list (admin-managed in Masters → Machines).
  const [tonnageOptions, setTonnageOptions] = useState([]);
  const [machineCosts, setMachineCosts] = useState({});  // { machineName: costPerPc }

  // Round 21.7 — Load Operations Bank for the "+ Add process step" dropdown
  useEffect(() => {
    api.get("/operations").then((r) => {
      setMasterProcs(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setMasterProcs([]));
  }, []);

  // Create a brand-new operation from the process picker (like "Add … as a new material").
  const createOperation = async (name) => {
    const nm = (name || "").trim();
    if (!nm) return null;
    // reuse an existing op if the name already exists (case-insensitive)
    const existing = masterProcs.find((m) => (m.name || "").trim().toLowerCase() === nm.toLowerCase());
    if (existing) return existing;
    try {
      const r = await api.post("/operations", { name: nm, default_cost_mode: "per_piece" });
      setMasterProcs((prev) => [...prev, r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      toast.success(`Added process "${nm}"`);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add process");
      return null;
    }
  };

  // Backfill the auto tonnage/station onto EXISTING rows that have none yet, using the
  // operation master (explicit default_tonnage) or the name rule — so setting a station in
  // the master reflects on parts added earlier, without overriding any tonnage already set.
  useEffect(() => {
    if (disabled || !masterProcs.length || !(items || []).length) return;
    let changed = false;
    const next = items.map((p) => {
      if (p.tonnage && String(p.tonnage).trim()) return p;  // respect an existing choice
      const name = p.name || "";
      const op = masterProcs.find((m) => (m.name || "") === name);
      const station = (op?.default_tonnage || "").trim() || autoTonnageForProcess(name);
      if (station) { changed = true; return { ...p, tonnage: station, no_machine: false }; }
      // Shearing / master no-machine → mark no_machine so it isn't flagged as missing tonnage.
      if ((/shearing|slitting/i.test(name) || op?.no_machine === true) && !p.no_machine) {
        changed = true; return { ...p, no_machine: true, tonnage: "" };
      }
      return p;  // press step — engineer picks the tonnage
    });
    if (changed) onChange(next);
  }, [masterProcs, items, disabled, onChange]);

  // Load the internal Machines list (name + cost) for the per-row dropdown + cost auto-fill.
  useEffect(() => {
    api.get("/settings/public").then((r) => {
      let machines = [];
      const mraw = r.data?.machines;
      if (mraw) { try { machines = JSON.parse(mraw); } catch { machines = []; } }
      // Always include the manual/deburring/ultrasonic stations so auto-picked
      // tonnages render and can be chosen even if not in the Machines master.
      const withStations = (list) => {
        const out = [...list];
        STATION_TONNAGES.forEach((s) => { if (!out.includes(s)) out.push(s); });
        return out;
      };
      if (Array.isArray(machines) && machines.length > 0) {
        setTonnageOptions(withStations(machines.map((m) => m.name).filter(Boolean)));
        const map = {};
        machines.forEach((m) => { if (m.name) map[m.name] = Number(m.cost) || 0; });
        setMachineCosts(map);
      } else {
        // Legacy fallback: plain comma-separated tonnage names, no costs.
        const raw = r.data?.machine_tonnages;
        const list = Array.isArray(raw) ? raw : String(raw || "").split(",").map((s) => s.trim()).filter(Boolean);
        setTonnageOptions(withStations(list));
        setMachineCosts({});
      }
    }).catch(() => { setTonnageOptions([...STATION_TONNAGES]); setMachineCosts({}); });
  }, []);

  // Click-outside to close the dropdown
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addCustomRow = () => {
    onChange([...(items || []), { name: "", cost_mode: "per_piece", rate: 0, cost: 0, is_default: false }]);
    setMenuOpen(false);
  };
  // Kept on purpose — the master-linkage add helper. The per-row Process dropdown now also
  // carries the master linkage (name + default mode/rate). Guard the unused-after-menu-removal warning.
  // eslint-disable-next-line no-unused-vars
  const addFromMaster = (mp) => {
    onChange([
      ...(items || []),
      {
        name: mp.name || "",
        // Round 21.7 — tolerate both operation-shape (default_cost_mode/default_rate) and legacy process-shape (cost_mode/rate)
        cost_mode: mp.default_cost_mode || mp.cost_mode || "per_piece",
        rate: Number(mp.default_rate ?? mp.rate) || 0,
        cost: 0,
        is_default: false,
      },
    ]);
    setMenuOpen(false);
  };
  const move = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = [...items];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };

  // Drag-and-drop reorder (native HTML5, dependency-free). Gated to start from the
  // grip handle via `handleActive`. Reorder only changes row ORDER + #; no calc change.
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const handleActiveRef = useRef(false);
  const reorder = (from, to) => {
    if (from == null || to == null || from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };
  const resetDrag = () => { setDragIndex(null); setOverIndex(null); handleActiveRef.current = false; };

  // Round 43 — Shearing/Slitting bills on gross weight (cut the full sheet
  // including scrap edge-trim). Substring match catches "Shearing/Slitting",
  // "Sheet Shearing", "Strip Slitting", etc.
  const GROSS_WEIGHT_PROCESS_TOKENS = ["shearing", "slitting"];
  const isGrossWeightProcess = (it) => {
    const name = (it.name || "").trim().toLowerCase();
    return GROSS_WEIGHT_PROCESS_TOKENS.some((tok) => name.includes(tok));
  };
  const procWeight = (it) =>
    isGrossWeightProcess(it) ? (Number(grossWeight) || 0) : (Number(netWeight) || 0);
  const procWeightLabel = (it) => (isGrossWeightProcess(it) ? "gross" : "net");

  const computeCostPerPc = (it) => {
    // MHR (time-based): ₹/pc = rate(₹/min) × cycle_time_sec ÷ 60.
    if (it.use_mhr) return (Number(it.rate) || 0) * (Number(it.cycle_time_sec) || 0) / 60;
    if ((it.cost_mode || "per_piece") === "per_kg") {
      return (Number(it.rate) || 0) * procWeight(it);
    }
    return Number(it.cost) || (Number(it.rate) || 0);
  };

  // Header MHR toggle. Turning it OFF clears every row's MHR flag so costs revert
  // to per-piece / per-kg; turning it ON just reveals the per-row tick boxes.
  const toggleMhrMode = (on) => {
    setMhrMode(on);
    if (!on && !disabled) {
      const next = (items || []).map((it) => (it.use_mhr ? { ...it, use_mhr: false } : it));
      if (next.some((n, i) => n !== items[i])) onChange(next);
    }
  };

  return (
    <div className="col-span-2" data-testid="processes-editor">
      {/* MHR mode toggle — corner of the Process Cost header. Ticking it reveals the
          per-row MHR tick boxes for CNC / VMC / Sliding-Head steps. */}
      <div className="flex items-center justify-end mb-2">
        <label
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-semibold cursor-pointer select-none ${mhrMode ? "border-indigo-400 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300"} ${!anyMhrEligible ? "opacity-50 cursor-not-allowed" : ""}`}
          title={anyMhrEligible
            ? "MHR (Machine-Hour Rate): tick to show the MHR check-boxes in front of CNC / VMC / Sliding-Head steps, then tick a step to bill it by time."
            : "No CNC / VMC / Sliding-Head step yet. Pick a CNC/VMC/Sliding-Head machine in the Tonnage column to enable MHR."}
        >
          <input
            type="checkbox"
            checked={mhrMode}
            disabled={disabled || !anyMhrEligible}
            onChange={(e) => toggleMhrMode(e.target.checked)}
            className="w-4 h-4 accent-indigo-600 cursor-pointer disabled:cursor-not-allowed"
            data-testid="process-mhr-mode"
          />
          <span>MHR</span>
          {mhrMode && <span className="text-[10px] font-normal text-indigo-500">— tick the CNC/VMC/Sliding-Head steps below</span>}
        </label>
      </div>
      <table className="w-full text-sm border border-slate-200 rounded overflow-visible">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="w-6"></th>
            <th className="text-left p-2 w-8">#</th>
            <th className="text-left p-2">Process</th>
            {isMulti && (
              <th className="text-center p-2 w-24 leading-tight text-orange-600" title={`Tick the steps that run ${cav}-up — one cycle makes ${cav} pieces, so their machine-hours are divided by ${cav}`}>
                Is it multi<br />cavity?
              </th>
            )}
            <th className="text-left p-2 w-28">Mode</th>
            <th className="text-left p-2 w-28">Tonnage</th>
            {hasMhr && (
              <th className="text-center p-2 w-16 leading-tight text-indigo-600" title="MHR = Machine-Hour Rate. Tick on CNC/VMC/sliding-head steps to bill by time: ₹/pc = rate(₹/min) × cycle time ÷ 60.">
                MHR
              </th>
            )}
            <th className="text-right p-2 w-24" title="Cycle time in seconds per piece — for capacity planning (no cost effect)">Cycle Time</th>
            <th className="text-right p-2 w-28">Rate</th>
            <th className="text-right p-2 w-28">₹/pc</th>
            <th className="w-20"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.length === 0 && (
            <tr>
              <td colSpan={isMulti ? 10 : 9} className="p-6 text-center text-sm text-slate-400">
                No process steps. {!disabled && "Click \"Add process step\" below."}
              </td>
            </tr>
          )}
          {items.map((it, idx) => {
            const mode = it.cost_mode || "per_piece";
            const isPerKg = mode === "per_kg";
            const costPerPc = computeCostPerPc(it);
            return (
              <tr
                key={idx}
                draggable={!disabled}
                onDragStart={(e) => {
                  if (!handleActiveRef.current) { e.preventDefault(); return; }
                  setDragIndex(idx);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragOver={(e) => {
                  if (dragIndex == null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  if (overIndex !== idx) setOverIndex(idx);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  reorder(dragIndex, idx);
                  resetDrag();
                }}
                onDragEnd={resetDrag}
                className={`hover:bg-slate-50 ${dragIndex === idx ? "opacity-40" : ""} ${overIndex === idx && dragIndex !== idx ? "border-t-2 border-orange-500" : ""}`}
              >
                <td className="p-1.5 w-6 text-center align-middle">
                  {!disabled && (
                    <span
                      onMouseDown={() => { handleActiveRef.current = true; }}
                      onMouseUp={() => { handleActiveRef.current = false; }}
                      className="inline-flex text-slate-300 hover:text-orange-600 cursor-grab active:cursor-grabbing"
                      title="Drag to reorder"
                      data-testid={`process-drag-${idx}`}
                    >
                      <GripVertical size={14} />
                    </span>
                  )}
                </td>
                <td className="p-1.5 text-slate-400 font-mono text-xs text-center">{idx + 1}</td>
                <td className="p-1.5">
                  <ProcessNamePicker
                    value={it.name || ""}
                    disabled={disabled}
                    options={masterProcs}
                    testId={`process-name-${idx}`}
                    onCreate={createOperation}
                    onChange={(val) => {
                      const mp = masterProcs.find((m) => (m.name || "") === val);
                      if (mp) {
                        // Picking a master operation pulls its default mode, rate, cycle time,
                        // and machine-op flag (cycle time is mandatory for machine operations).
                        // Shearing/Slitting is a non-machine step (bills on gross weight) — auto-mark it.
                        const isShear = /shearing|slitting/i.test(mp.name || "");
                        // A station op runs ON that station, so it is NOT "No machine" even if the
                        // operation master flags it non-press. An explicit "default_tonnage" set on
                        // the operation master wins over the name-based rule.
                        const auto = (mp.default_tonnage || "").trim() || autoTonnageForProcess(mp.name);
                        // "No machine" is driven by the operation master (Settings → Operations):
                        // its explicit no_machine flag, Shearing/Slitting, or machine_op off.
                        const noMachine = auto ? false : (isShear || mp.no_machine === true || mp.machine_op === false);
                        const patch = {
                          name: mp.name,
                          cost_mode: mp.default_cost_mode || mp.cost_mode || "per_piece",
                          rate: Number(mp.default_rate ?? mp.rate) || 0,
                          cost: 0,
                          machine_op: !!mp.machine_op,
                          machine_type: mp.machine_op ? (mp.machine_type || "") : "",
                          no_machine: noMachine,
                        };
                        if (noMachine) { patch.tonnage = ""; }
                        else if (auto) {
                          patch.tonnage = auto;
                          const c = machineCosts[auto];
                          if (c != null && Number(c) > 0) { patch.cost = Number(c); patch.cost_mode = "per_piece"; }
                        }
                        if (isShear) { patch.cycle_time_sec = 0; }
                        const dct = Number(mp.default_cycle_time_sec) || 0;
                        if (dct > 0 && !isShear) patch.cycle_time_sec = dct;  // user can still override
                        // Blanking/forming press ops run N-up in a multi-cavity tool → pre-tick.
                        if (it.multi_cavity == null) patch.multi_cavity = isBlankingOp(mp.name);
                        update(idx, patch);
                      } else {
                        const patch = { name: val };
                        const auto = autoTonnageForProcess(val);
                        if (auto) { patch.tonnage = auto; patch.no_machine = false; }
                        if (it.multi_cavity == null) patch.multi_cavity = isBlankingOp(val);
                        update(idx, patch);
                      }
                    }}
                  />
                </td>
                {isMulti && (
                  <td className="p-1.5 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!!it.multi_cavity}
                        disabled={disabled}
                        onChange={(e) => update(idx, { multi_cavity: e.target.checked, cavities: e.target.checked ? (Number(it.cavities) || 2) : it.cavities })}
                        className="w-4 h-4 accent-orange-600 cursor-pointer align-middle"
                        title="Runs N-up (multi-cavity) — tick and choose how many cavities for this step"
                        data-testid={`process-cavity-${idx}`}
                      />
                      {it.multi_cavity && (
                        <select
                          value={Math.min(Number(it.cavities) || 2, cav)}
                          disabled={disabled}
                          onChange={(e) => update(idx, { cavities: Math.max(2, Number(e.target.value) || 2) })}
                          className="border border-slate-300 rounded px-1 py-0.5 text-xs"
                          title={`Cavities for this process (2 … ${cav}) — machine-hours ÷ this number`}
                          data-testid={`process-cavity-count-${idx}`}
                        >
                          {Array.from({ length: Math.max(1, cav - 1) }, (_, i) => i + 2).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      )}
                    </div>
                  </td>
                )}
                <td className="p-1.5">
                  {it.use_mhr ? (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded" title="Time-based: ₹/pc = rate (₹/min) × cycle time ÷ 60">MHR · ₹/min</span>
                  ) : (
                    <select
                      value={mode}
                      disabled={disabled}
                      onChange={(e) => {
                        const newMode = e.target.value;
                        update(idx, { cost_mode: newMode, rate: 0, cost: 0 });
                      }}
                      className="w-full px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                      data-testid={`process-mode-${idx}`}
                    >
                      <option value="per_piece">₹ per piece</option>
                      <option value="per_kg">₹ per kg</option>
                    </select>
                  )}
                </td>
                <td className="p-1.5">
                  {/* Internal machine tonnage tag — no cost effect; hidden from customer quote/Excel.
                      "No machine" (e.g. Shearing) is set automatically from the operation master —
                      such steps carry no tonnage and are excluded from Machine Requirement. */}
                  <div className="flex items-center gap-1">
                    {it.no_machine ? (
                      <span className="text-[11px] text-slate-400 italic" title="Non-machine step — excluded from Machine Requirement">No machine</span>
                    ) : (
                      <>
                        <select
                          value={it.tonnage || ""}
                          disabled={disabled}
                          onChange={(e) => {
                            const ton = e.target.value;
                            const patch = { tonnage: ton };
                            // Auto-fill ₹/pc from the Machines master when the picked machine has a cost.
                            const c = machineCosts[ton];
                            if (c != null && Number(c) > 0) { patch.cost = Number(c); patch.cost_mode = "per_piece"; }
                            update(idx, patch);
                          }}
                          className={`w-28 shrink-0 px-2 py-1 border rounded text-sm bg-white ${!it.tonnage ? "border-red-300" : "border-slate-200"}`}
                          data-testid={`process-tonnage-${idx}`}
                        >
                          <option value="">—</option>
                          {/* Keep the current value selectable even if it's a legacy/custom
                              station (e.g. "Manual" saved before the Manpower rename). */}
                          {it.tonnage && !tonnageOptions.includes(it.tonnage) && (
                            <option value={it.tonnage}>{it.tonnage}</option>
                          )}
                          {tonnageOptions.map((t, ti) => (
                            <option key={ti} value={t}>{t}</option>
                          ))}
                        </select>
                        {it.machine_op && it.machine_type && (
                          <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 uppercase" title="Machine type">
                            {MACHINE_TYPE_SHORT[it.machine_type] || it.machine_type}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </td>
                {hasMhr && (
                  <td className="p-1.5 text-center">
                    {isMhrType(it) ? (
                      <input
                        type="checkbox"
                        checked={!!it.use_mhr}
                        disabled={disabled}
                        onChange={(e) => update(idx, e.target.checked
                          ? { use_mhr: true, rate: Number(it.rate) || Number(it.cost) || 0, cost_mode: "per_piece" }
                          : { use_mhr: false })}
                        className="w-4 h-4 accent-indigo-600 cursor-pointer align-middle"
                        title="Bill by Machine-Hour Rate — type the ₹/min in the Rate column; ₹/pc = rate × cycle ÷ 60"
                        data-testid={`process-mhr-${idx}`}
                      />
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                )}
                <td className="p-1.5">
                  {/* Cycle time (sec/pc) — internal capacity-planning input; no cost effect.
                      Available on non-machine steps too (Visual / Deburring need a cycle time). */}
                  <DecimalInput
                    disabled={disabled}
                    value={it.cycle_time_sec ?? 0}
                    onChange={(v) => update(idx, { cycle_time_sec: v })}
                    className="w-full px-2 py-1 border border-slate-200 rounded text-sm font-mono text-right"
                    data-testid={`process-cycle-${idx}`}
                    title="Cycle time (seconds per piece)"
                  />
                </td>
                <td className="p-1.5">
                  <DecimalInput
                    disabled={disabled}
                    value={it.use_mhr ? (it.rate ?? 0) : (isPerKg ? (it.rate ?? 0) : (it.cost ?? 0))}
                    onChange={(v) => {
                      if (it.use_mhr) update(idx, { rate: v });
                      else if (isPerKg) update(idx, { rate: v });
                      else update(idx, { cost: v, rate: v });
                    }}
                    className={`w-full px-2 py-1 border rounded text-sm font-mono text-right ${it.use_mhr ? "border-indigo-300 bg-indigo-50/40" : "border-slate-200"}`}
                    data-testid={`process-rate-${idx}`}
                    title={it.use_mhr ? "₹ per minute (MHR) — type it here" : (isPerKg ? "₹ per kg" : "₹ per piece")}
                  />
                </td>
                <td className="p-1.5 font-mono text-right text-slate-700">
                  {costPerPc.toFixed(4)}
                  {it.use_mhr ? (
                    <div className="text-[10px] text-indigo-400 leading-tight">
                      = {(Number(it.rate) || 0).toFixed(2)}/min × {(Number(it.cycle_time_sec) || 0)}s ÷ 60
                    </div>
                  ) : isPerKg && (
                    <div className="text-[10px] text-slate-400 leading-tight">
                      = {(Number(it.rate) || 0).toFixed(2)} × {procWeight(it).toFixed(4)} kg ({procWeightLabel(it)})
                    </div>
                  )}
                </td>
                <td className="p-1.5 w-20 whitespace-nowrap">
                  {!disabled && (
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => move(idx, -1)}
                        disabled={idx === 0}
                        className="text-slate-300 hover:text-orange-600 disabled:opacity-30"
                        title="Move up"
                        data-testid={`process-up-${idx}`}
                      >
                        <ArrowUp size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => move(idx, 1)}
                        disabled={idx === items.length - 1}
                        className="text-slate-300 hover:text-orange-600 disabled:opacity-30"
                        title="Move down"
                        data-testid={`process-down-${idx}`}
                      >
                        <ArrowDown size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(idx)}
                        className="text-slate-300 hover:text-red-500"
                        title="Remove"
                        data-testid={`process-remove-${idx}`}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="border-t border-slate-200 p-2 flex justify-between items-center bg-slate-50">
        {!disabled ? (
          <button
            type="button"
            onClick={addCustomRow}
            disabled={disabled}
            className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1 disabled:opacity-50"
            data-testid="add-process-btn"
          >
            <Plus size={14} /> Add process step
          </button>
        ) : <div />}
        <div className="text-sm">
          <span className="text-slate-500">Total Process Cost (B):</span>{" "}
          <strong className="font-mono text-slate-900">
            ₹{(Number(total) || 0).toFixed(4)} / pc
          </strong>
        </div>
      </div>
      {(() => {
        // Capacity estimate: total cycle time per press (tonnage), and machine-hours/month at peak volume.
        const load = {};
        (items || []).forEach((it) => {
          let ct = Number(it.cycle_time_sec) || 0;
          if (ct <= 0) return;
          // Multi-cavity step runs N-up → per-piece machine time is ct / (this step's cavities).
          if (it.multi_cavity) ct = ct / Math.max(2, Number(it.cavities) || cav || 2);
          const press = it.tonnage || "(no press)";
          load[press] = (load[press] || 0) + ct;
        });
        const rows = Object.entries(load);
        if (rows.length === 0) return null;
        return (
          <div className="border-t border-slate-200 px-2 py-2 bg-white">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              Capacity estimate (by press)
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              {rows.map(([press, sec]) => (
                <div key={press} className="px-2 py-1 rounded border border-slate-200 bg-slate-50" data-testid={`capacity-${press}`}>
                  <span className="font-semibold text-slate-700">{press}</span>
                  <span className="text-slate-500"> · {sec.toFixed(1)} s/pc</span>
                  {monthlyVolume > 0 && (
                    <span className="text-slate-500"> · {(sec * monthlyVolume / 3600).toLocaleString("en-IN", { maximumFractionDigits: 0 })} hrs/mo</span>
                  )}
                </div>
              ))}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">
              {isMulti && <span className="text-orange-600">{cav}-cavity: ticked steps count ÷{cav} (N-up). </span>}
              {monthlyVolume > 0
                ? `Machine-hours/month at peak volume (${Number(monthlyVolume).toLocaleString("en-IN")} pcs/mo). Divide a press's hrs/mo by its available hours/month to estimate machines required.`
                : "Add Project Scope volumes to also see machine-hours/month per press."}
            </div>
          </div>
        );
      })()}
    </div>
  );
}


function ItemListEditor({ items, onChange, columns, totalLabel, total, addLabel, testId, disabled }) {
  const update = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  const add = () => onChange([...items, columns.reduce((acc, c) => ({ ...acc, [c.key]: c.type === "number" ? 0 : "" }), {})]);
  const move = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    const next = [...items];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    onChange(next);
  };
  return (
    <div className="col-span-2" data-testid={testId}>
      <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="text-left p-2 w-8">#</th>
            {columns.map((c) => <th key={c.key} className={`text-left p-2 ${c.width || ""}`}>{c.label}</th>)}
            <th className="w-8"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {items.length === 0 && (
            <tr><td colSpan={columns.length + 2} className="p-6 text-center text-sm text-slate-400">No items. {!disabled && "Click “Add” below."}</td></tr>
          )}
          {items.map((it, idx) => (
            <tr key={idx} className="hover:bg-slate-50">
              <td className="p-1.5 text-slate-400 font-mono text-xs text-center">{idx + 1}</td>
              {columns.map((c) => (
                <td key={c.key} className="p-1.5">
                  <input
                    type={c.type}
                    step={c.type === "number" ? "0.0001" : undefined}
                    disabled={disabled}
                    value={it[c.key] ?? (c.type === "number" ? 0 : "")}
                    onChange={(e) => update(idx, { [c.key]: c.type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value })}
                    className={`w-full px-2 py-1 border border-slate-200 rounded text-sm ${c.type === "number" ? "font-mono text-right" : ""}`}
                  />
                </td>
              ))}
              <td className="p-1.5 w-20 whitespace-nowrap">
                {!disabled && (
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => move(idx, -1)}
                      disabled={idx === 0}
                      className="text-slate-300 hover:text-orange-600 disabled:opacity-30 disabled:hover:text-slate-300"
                      title="Move up"
                      data-testid={`row-up-${idx}`}
                    >
                      <ArrowUp size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(idx, 1)}
                      disabled={idx === items.length - 1}
                      className="text-slate-300 hover:text-orange-600 disabled:opacity-30 disabled:hover:text-slate-300"
                      title="Move down"
                      data-testid={`row-down-${idx}`}
                    >
                      <ArrowDown size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(idx)}
                      className="text-slate-300 hover:text-red-500"
                      title="Delete row"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between mt-2">
        {!disabled ? (
          <button type="button" onClick={add} className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"><Plus size={14} /> {addLabel}</button>
        ) : <div />}
        {totalLabel && (
          <div className="text-sm">
            <span className="text-slate-500">{totalLabel}:</span>{" "}
            <strong className="font-mono text-slate-900">{formatINR(total)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
